import { AnimatePresence, motion } from 'framer-motion';
import {
    Activity,
    AlertTriangle,
    ArrowRight,
    BookOpen,
    Check,
    ChevronDown,
    ChevronUp,
    CircleDot,
    Compass,
    Crosshair,
    Download,
    Eye,
    FileText,
    GitBranch,
    HelpCircle,
    Info,
    Layers,
    Play,
    Save,
    Settings,
    Sparkles,
    Square,
    Target,
    X,
    Zap
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import AxisRadarChart from './AxisRadarChart.jsx';
import Livret from './Livret.js';

import {
    getScenariosForDomain
} from './domainBridge.js';
import { livretContextMap } from './livretData.js';
import posturesVisualization3DData from './posturesData2.js';
import {
    listSessions,
    saveSession
} from './sessionManager.js';
import {
    appendPosition,
    appendScenarioResult,
    createTrace,
    downloadTrace,
    finalizeTrace,
    signTrace,
    summarizeTrace,
    verifyTrace
} from './traceExporter.js';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const DESIGN_SYSTEM = {
  colors: {
    bg: '#06060f',
    bgPanel: '#0a0a18',
    bgCard: '#0d0d1e',
    bgInset: '#080814',
    grid: 'rgba(167, 139, 250, 0.03)',
    border: 'rgba(167, 139, 250, 0.10)',
    borderHover: 'rgba(167, 139, 250, 0.25)',
    borderActive: 'rgba(167, 139, 250, 0.55)',
    violet: '#a78bfa',
    violetDim: '#6d5dd3',
    violetFaint: 'rgba(167, 139, 250, 0.08)',
    cyan: '#22d3ee',
    cyanDim: '#0e7490',
    cyanFaint: 'rgba(34, 211, 238, 0.08)',
    green: '#34d399',
    greenDim: '#059669',
    greenFaint: 'rgba(52, 211, 153, 0.08)',
    amber: '#fbbf24',
    amberFaint: 'rgba(251, 191, 36, 0.10)',
    orange: '#f97316',
    orangeFaint: 'rgba(249, 115, 22, 0.10)',
    red: '#ef4444',
    redDim: '#991b1b',
    redFaint: 'rgba(239, 68, 68, 0.10)',
    rose: '#f43f5e',
    slate: '#94a3b8',
    slateFaint: 'rgba(148, 163, 184, 0.08)',
    white: '#f5f7ff',
    whiteDim: '#8892b0',
    textPrimary: '#e2ecff',
    textSecond: '#dadada',
    textMuted: '#afafaf',
    textAccent: '#a78bfa',
  },
  fonts: {
    mono: '"JetBrains Mono", "Fira Code", monospace',
    display: '"Syne", "Space Grotesk", sans-serif',
    body: '"Inter", system-ui, sans-serif',
  },
};

const C = DESIGN_SYSTEM.colors;
const MONO = DESIGN_SYSTEM.fonts.mono;
const DISPLAY = DESIGN_SYSTEM.fonts.display;
const BODY = DESIGN_SYSTEM.fonts.body;

// ═══════════════════════════════════════════════════════════════════════════════
// PERFORMANCE TIER
// ═══════════════════════════════════════════════════════════════════════════════

const detectPerfTier = () => {
  if (typeof window === 'undefined') return 'high';

  const cores = navigator.hardwareConcurrency || 4;
  const mem = navigator.deviceMemory || 4; // Chrome-only, undefined ailleurs
  const isCoarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches;
  const dpr = window.devicePixelRatio || 1;

  // Heuristique simple : mobile / peu de coeurs / peu de mémoire → tier bas
  let score = 0;
  if (cores >= 8) score += 2; else if (cores >= 4) score += 1;
  if (mem >= 8) score += 2; else if (mem >= 4) score += 1;
  if (!isCoarsePointer) score += 1;
  if (dpr <= 2) score += 1;

  if (score >= 5) return 'high';
  if (score >= 3) return 'medium';
  return 'low';
};

const PERF_PROFILES = {
  high: {
    pixelRatioCap: 2,
    shadowMapSize: 2048,
    shadowsEnabled: true,
    icoDetailArchetype: 4,
    icoDetailActor: 3,
    particleCount: 220,
    bloomResolutionScale: 1,
    bloomStrength: 0.8,
  },
  medium: {
    pixelRatioCap: 1.5,
    shadowMapSize: 1024,
    shadowsEnabled: true,
    icoDetailArchetype: 3,
    icoDetailActor: 2,
    particleCount: 120,
    bloomResolutionScale: 0.75,
    bloomStrength: 0.65,
  },
  low: {
    pixelRatioCap: 1,
    shadowMapSize: 512,
    shadowsEnabled: false,
    icoDetailArchetype: 1,
    icoDetailActor: 1,
    particleCount: 60,
    bloomResolutionScale: 0.5,
    bloomStrength: 0.5,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITAIRES — Projection 6D ↔ 3D
// ═══════════════════════════════════════════════════════════════════════════════

const positionTo3D = (position, config) => {
  // scale relevé de 5 à 7 : avec 10 archétypes répartis sur 6 dimensions,
  // plusieurs paires se retrouvaient à ~1,7-1,8 unité l'une de l'autre (ex.
  // Pilote/Agitateur, Architecte/Sage) — trop proche pour des sphères + halos
  // + labels toujours affichés, qui se chevauchent visuellement. +40%
  // d'espacement uniforme, sans toucher lumières, grille ni axes.
  const scale = 7;
  const get = (id) => Math.max(0, Math.min(1, position[id] ?? 0.5));

  const x1 = (get('posture') - 0.5) * 2 * scale;
  const y1 = (get('temps') - 0.5) * 2 * scale;
  const z1 = (get('autre') - 0.5) * 2 * scale;

  const x2 = (get('mesure') - 0.5) * 2 * scale * 0.4;
  const y2 = (get('erreur') - 0.5) * 2 * scale * 0.4;
  const z2 = (get('systeme') - 0.5) * 2 * scale * 0.4;

  return new THREE.Vector3(x1 + x2, y1 + y2, z1 + z2);
};

const position3DTo6D = (worldPosition, currentPosition = {}) => {
  // Doit rester strictement identique à celle de positionTo3D — c'est
  // l'inverse de cette projection, utilisée quand on glisse "vous" dans la scène.
  const scale = 7;

  const x2 = ((currentPosition.mesure ?? 0.5) - 0.5) * 2 * scale * 0.4;
  const y2 = ((currentPosition.erreur ?? 0.5) - 0.5) * 2 * scale * 0.4;
  const z2 = ((currentPosition.systeme ?? 0.5) - 0.5) * 2 * scale * 0.4;

  const x1 = worldPosition.x - x2;
  const y1 = worldPosition.y - y2;
  const z1 = worldPosition.z - z2;

  return {
    posture: Math.max(0, Math.min(1, x1 / (scale * 2) + 0.5)),
    temps: Math.max(0, Math.min(1, y1 / (scale * 2) + 0.5)),
    autre: Math.max(0, Math.min(1, z1 / (scale * 2) + 0.5)),
    mesure: currentPosition.mesure ?? 0.5,
    erreur: currentPosition.erreur ?? 0.5,
    systeme: currentPosition.systeme ?? 0.5,
  };
};

const distance6D = (a, b) => {
  const keys = ['posture', 'temps', 'autre', 'mesure', 'erreur', 'systeme'];
  let sum = 0;
  keys.forEach(k => {
    const d = (a[k] ?? 0.5) - (b[k] ?? 0.5);
    sum += d * d;
  });
  return Math.sqrt(sum / keys.length);
};

// Magnétisme
const MAGNETIC_STEP = 0.02;
const snapPosition6D = (position, step = MAGNETIC_STEP) => {
  const snapped = {};
  Object.keys(position).forEach(k => {
    const v = position[k];
    snapped[k] = typeof v === 'number' ? Math.max(0, Math.min(1, Math.round(v / step) * step)) : v;
  });
  return snapped;
};


const getClosestScenarioBehavior = (config, scenario, position) => {
  let closest = null;
  let minDist = Infinity;
  config.archetypes?.forEach(a => {
    if (!scenario.behaviors?.[a.id]) return;
    const d = distance6D(position, a.position);
    if (d < minDist) { minDist = d; closest = a; }
  });
  if (!closest) return null;
  return { archetype: closest, behavior: scenario.behaviors[closest.id], distance: minDist };
};

const RISK_COLOR = { critical: C.red, moderate: C.amber, low: C.green };

// ═══════════════════════════════════════════════════════════════════════════════
// CRÉATION DE SCÈNE
// ═══════════════════════════════════════════════════════════════════════════════

const createPosturesScene = (container, config, perf) => {
  const W = container.clientWidth;
  const H = container.clientHeight;
  if (W === 0 || H === 0) return null;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(config.ambiance?.background || C.bg);

  if (config.ambiance?.fog?.enabled) {
    const fog = config.ambiance.fog;
    scene.fog = new THREE.Fog(
      new THREE.Color(fog.color || C.bg),
      fog.near || 25,
      fog.far || 55
    );
  }

  const camera = new THREE.PerspectiveCamera(50, W / H, 0.1, 200);
  camera.position.set(
    config.camera?.initialPosition?.x ?? 0,
    config.camera?.initialPosition?.y ?? 5,
    config.camera?.initialPosition?.z ?? 20
  );

  const renderer = new THREE.WebGLRenderer({
    antialias: perf.pixelRatioCap <= 1.5, // AA coûteux : seulement si pixelRatio limité
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, perf.pixelRatioCap));
  renderer.setSize(W, H);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  // OMBRES — désactivées automatiquement sur les appareils faibles
  renderer.shadowMap.enabled = perf.shadowsEnabled;
  renderer.shadowMap.type = THREE.PCFShadowMap;

  container.appendChild(renderer.domElement);
  renderer.domElement.style.display = 'block';
  renderer.domElement.style.position = 'absolute';
  renderer.domElement.style.top = 0;
  renderer.domElement.style.left = 0;
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  renderer.domElement.style.pointerEvents = 'auto';

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  // Bloom — résolution réduite sur les tiers moyens/bas, et rendu direct à l'écran
  // (suppression du ShaderPass(CopyShader) redondant : une passe de moins par frame)
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(W * perf.bloomResolutionScale, H * perf.bloomResolutionScale),
    perf.bloomStrength,
    0.6,
    0.95
  );
  bloomPass.renderToScreen = true;
  composer.addPass(bloomPass);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = 5;
  controls.maxDistance = 45;
  controls.target.set(
    config.camera?.lookAt?.x ?? 0,
    config.camera?.lookAt?.y ?? 0,
    config.camera?.lookAt?.z ?? 0
  );
  controls.update();

  // ═══════════════════════════════════════════════════════════════════════════
  // ÉCLAIRAGE PREMIUM
  // ═══════════════════════════════════════════════════════════════════════════

  scene.add(new THREE.AmbientLight(0xffffff, 0.4));

  // Key light — principal, avec ombres
  const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
  keyLight.position.set(10, 14, 10);
  if (perf.shadowsEnabled) {
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = perf.shadowMapSize;
    keyLight.shadow.mapSize.height = perf.shadowMapSize;
    keyLight.shadow.camera.near = 0.5;
    keyLight.shadow.camera.far = 50;
    keyLight.shadow.camera.left = -20;
    keyLight.shadow.camera.right = 20;
    keyLight.shadow.camera.top = 20;
    keyLight.shadow.camera.bottom = -20;
    keyLight.shadow.bias = -0.0001;
  }
  scene.add(keyLight);

  // Rim light gauche — lumière d'accentuation
  const rimLightLeft = new THREE.DirectionalLight(0xa78bfa, 0.7);
  rimLightLeft.position.set(-8, 6, -10);
  scene.add(rimLightLeft);

  // Rim light droite — lumière de remplissage
  const rimLightRight = new THREE.DirectionalLight(0x22d3ee, 0.5);
  rimLightRight.position.set(-5, -2, 8);
  scene.add(rimLightRight);

  // Fill light bas
  const fillLight = new THREE.PointLight(0x66b3ff, 0.4);
  fillLight.position.set(0, -5, 0);
  scene.add(fillLight);

  // ═══════════════════════════════════════════════════════════════════════════
  // GRILLE & PLAN
  // ═══════════════════════════════════════════════════════════════════════════

  if (config.ambiance?.grid !== false) {
    // Grille raffinée
    const grid = new THREE.GridHelper(24, 48, C.white, C.white);
    grid.material.opacity = 0.04;
    grid.material.transparent = true;
    grid.material.depthWrite = false;
    grid.position.y = -0.5;
    scene.add(grid);

    // Plan avec shadow receiver
    const planeGeo = new THREE.PlaneGeometry(24, 24);
    const planeMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(C.white),
      transparent: true,
      opacity: 0.008,
      side: THREE.DoubleSide,
      depthWrite: false,
      metalness: 0.1,
      roughness: 0.9,
    });
    const plane = new THREE.Mesh(planeGeo, planeMat);
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = -0.5;
    plane.receiveShadow = perf.shadowsEnabled;
    scene.add(plane);

    // Axes centraux avec gradient
    const centerLineMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(C.white),
      transparent: true,
      opacity: 0.25,
      linewidth: 2,
    });

    const xLineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-12, 0, 0),
      new THREE.Vector3(12, 0, 0),
    ]);
    scene.add(new THREE.Line(xLineGeo, centerLineMat));

    const zLineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, -12),
      new THREE.Vector3(0, 0, 12),
    ]);
    scene.add(new THREE.Line(zLineGeo, centerLineMat));

    // Cercles concentriques amélorés
    [3, 6, 9].forEach((radius, i) => {
      const segments = 128;
      const points = [];
      for (let s = 0; s <= segments; s++) {
        const a = (s / segments) * Math.PI * 2;
        points.push(new THREE.Vector3(
          Math.cos(a) * radius,
          0,
          Math.sin(a) * radius
        ));
      }
      const circleGeo = new THREE.BufferGeometry().setFromPoints(points);
      const circleMat = new THREE.LineBasicMaterial({
        color: new THREE.Color(C.white),
        transparent: true,
        opacity: 0.04 + i * 0.02,
      });
      scene.add(new THREE.LineLoop(circleGeo, circleMat));
    });

    // Cercle focal au centre
    const focalSegments = 64;
    const focalPoints = [];
    for (let s = 0; s <= focalSegments; s++) {
      const a = (s / focalSegments) * Math.PI * 2;
      focalPoints.push(new THREE.Vector3(
        Math.cos(a) * 0.5,
        0,
        Math.sin(a) * 0.5
      ));
    }
    const focalGeo = new THREE.BufferGeometry().setFromPoints(focalPoints);
    const focalMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(C.violet),
      transparent: true,
      opacity: 0.15,
    });
    scene.add(new THREE.LineLoop(focalGeo, focalMat));
  }

  // Particules — SEUL système de particules de la scène (l'ancien doublon dans
  // PosturesSceneRenderer a été supprimé : on ne paie plus deux fois ce coût GPU).
  if (config.ambiance?.particles !== false) {
    const particleCount = perf.particleCount;
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(particleCount * 3);
    const size = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 40;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 40;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 40;
      size[i] = Math.random() * 0.08 + 0.02;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(size, 1));

    const particleShader = {
      vertex: `
        attribute float size;
        varying float vAlpha;

        void main() {
          vAlpha = size * 2.0;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragment: `
        varying float vAlpha;

        void main() {
          float dist = length(gl_PointCoord - vec2(0.5, 0.5));
          if (dist > 0.5) discard;

          float alpha = (1.0 - dist * dist) * vAlpha * 0.2;
          gl_FragColor = vec4(0.667, 0.533, 1.0, alpha);
        }
      `
    };

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        texture: { value: new THREE.Texture() }
      },
      vertexShader: particleShader.vertex,
      fragmentShader: particleShader.fragment,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    scene.add(new THREE.Points(geo, mat));
  }

  return {
    scene,
    camera,
    renderer,
    controls,
    composer,
    bloomPass,
    keyLight,
  };
};


const spriteTextureCache = new Map();

const getCachedLabelTexture = (text, color) => {
  const key = `${text}__${color}`;
  const cached = spriteTextureCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = 'bold 48px "JetBrains Mono", monospace';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
  ctx.shadowBlur = 8;
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  spriteTextureCache.set(key, texture);
  return texture;
};

const createSpriteLabel = (text, color, size = 1) => {
  const texture = getCachedLabelTexture(text, color);

  
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    opacity: 0.95,
  });

  const sprite = new THREE.Sprite(material);
  sprite.scale.set(2 * size, 0.5 * size, 1);
  sprite.renderOrder = 999;
  sprite.userData.baseOpacity = 0.95;
  return sprite;
};

// ═══════════════════════════════════════════════════════════════════════════════
// AXES — Représentations cartésiennes
// ═══════════════════════════════════════════════════════════════════════════════

const createAxisRepresentations = (scene, config) => {
  const axisObjects = [];
  const axisScale = 5;

  config.axes?.forEach((axis, index) => {
    const angle = (index / config.axes.length) * Math.PI * 2;
    const radius = 8;
    const centerX = Math.cos(angle) * radius * 0.3;
    const centerY = Math.sin(angle) * radius * 0.3;

    const poleA = new THREE.Vector3(
      centerX + Math.cos(angle) * axisScale * 0.6,
      centerY + Math.sin(angle) * axisScale * 0.6,
      0
    );
    const poleB = new THREE.Vector3(
      centerX - Math.cos(angle) * axisScale * 0.6,
      centerY - Math.sin(angle) * axisScale * 0.6,
      0
    );

    // Ligne de l'axe
    const lineGeo = new THREE.BufferGeometry().setFromPoints([poleA, poleB]);
    const lineMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(C.amber),
      transparent: true,
      opacity: 0.18,
    });
    const line = new THREE.Line(lineGeo, lineMat);
    line.userData.baseOpacity = 0.18;
    scene.add(line);
    axisObjects.push(line);

    // Graduations
    for (let t = 1; t < 5; t++) {
      const ratio = t / 5;
      const tickPos = poleA.clone().lerp(poleB, ratio);
      const dir = poleB.clone().sub(poleA).normalize();
      const perp = new THREE.Vector3(-dir.y, dir.x, dir.z).normalize();
      const tickLen = t === 2 || t === 3 ? 0.12 : 0.07;
      const tickA = tickPos.clone().addScaledVector(perp, tickLen);
      const tickB = tickPos.clone().addScaledVector(perp, -tickLen);

      const tickGeo = new THREE.BufferGeometry().setFromPoints([tickA, tickB]);
      const tickMat = new THREE.LineBasicMaterial({
        color: new THREE.Color(C.amber),
        transparent: true,
        opacity: 0.22,
      });
      const tick = new THREE.Line(tickGeo, tickMat);
      tick.userData.baseOpacity = 0.22;
      scene.add(tick);
      axisObjects.push(tick);
    }

    // Marqueurs de pôles
    const markerA = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 12, 12),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(C.amber),
        transparent: true,
        opacity: 0.9,
      })
    );
    markerA.position.copy(poleA);
    markerA.userData.baseOpacity = 0.9;
    scene.add(markerA);
    axisObjects.push(markerA);

    const markerB = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 12, 12),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(C.amber),
        transparent: true,
        opacity: 0.55,
      })
    );
    markerB.position.copy(poleB);
    markerB.userData.baseOpacity = 0.55;
    scene.add(markerB);
    axisObjects.push(markerB);

    // Labels
    const labelA = createSpriteLabel(
      axis.poleA.shortLabel || axis.poleA.label,
      C.white,
      0.9
    );
    labelA.position.copy(poleA);
    labelA.position.addScaledVector(
      new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0).normalize(),
      0.55
    );
    scene.add(labelA);
    axisObjects.push(labelA);

    const labelB = createSpriteLabel(
      axis.poleB.shortLabel || axis.poleB.label,
      C.whiteDim,
      0.9
    );
    labelB.position.copy(poleB);
    labelB.position.addScaledVector(
      new THREE.Vector3(-Math.cos(angle), -Math.sin(angle), 0).normalize(),
      0.55
    );
    scene.add(labelB);
    axisObjects.push(labelB);

    // Nom de l'axe au centre
    const axisLabel = createSpriteLabel(
      axis.shortLabel?.toUpperCase() || axis.id.toUpperCase(),
      C.whiteDim,
      0.65
    );
    const centerPos = poleA.clone().lerp(poleB, 0.5);
    axisLabel.position.copy(centerPos);
    axisLabel.position.z += 0.3;
    scene.add(axisLabel);
    axisObjects.push(axisLabel);
  });

  return axisObjects;
};

// ═══════════════════════════════════════════════════════════════════════════════
// ARCHÉTYPES — MATÉRIAUX PREMIUM
// ═══════════════════════════════════════════════════════════════════════════════

const createArchetype = (archetype, config, perf) => {
  const group = new THREE.Group();
  const position = positionTo3D(archetype.position, config);
  group.position.copy(position);

  const color = archetype.color;
  const radius = 0.35;
  
  const geo = new THREE.IcosahedronGeometry(radius, perf.icoDetailArchetype);

  const mat = new THREE.MeshPhysicalMaterial({
    color: color,
    metalness: 0.2,
    roughness: 0.4,
    clearcoat: 0.8,
    clearcoatRoughness: 0.15,
    emissive: color,
    emissiveIntensity: 0.35,
    transparent: true,
    opacity: 0.95,
    ior: 1.5,
    thickness: 0.5,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow = perf.shadowsEnabled;
  mesh.receiveShadow = perf.shadowsEnabled;
  mesh.userData.originalColor = color;
  mesh.userData.baseEmissive = 0.35;
  group.add(mesh);

  // Halo étendu (plus grand et plus lumineux)
  const haloGeo = new THREE.SphereGeometry(radius * 2, 20, 20);
  const haloMat = new THREE.MeshBasicMaterial({
    color: color,
    transparent: true,
    opacity: 0.08,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const halo = new THREE.Mesh(haloGeo, haloMat);
  halo.userData.originalColor = color;
  group.add(halo);

  // Anneau avec meilleur détail
  const ringGeo = new THREE.TorusGeometry(radius * 1.5, 0.02, 12, 128);
  const ringMat = new THREE.MeshStandardMaterial({
    color: color,
    transparent: true,
    opacity: 0.5,
    metalness: 0.6,
    roughness: 0.2,
    emissive: color,
    emissiveIntensity: 0.2,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.rotation.x = Math.PI / 3;
  ring.castShadow = perf.shadowsEnabled;
  ring.userData.originalColor = color;
  group.add(ring);

  // Anneau de rotation secondaire
  const ringGeo2 = new THREE.TorusGeometry(radius * 1.8, 0.012, 6, 128);
  const ringMat2 = new THREE.MeshStandardMaterial({
    color: color,
    transparent: true,
    opacity: 0.3,
    metalness: 0.4,
    roughness: 0.4,
  });
  const ring2 = new THREE.Mesh(ringGeo2, ringMat2);
  ring2.rotation.y = Math.PI / 4;
  ring2.rotation.z = Math.PI / 5;
  group.add(ring2);

  // Label
  const label = createSpriteLabel(archetype.label || archetype.shortLabel, color, 1.2);
  label.position.y = radius + 0.7;
  group.add(label);

  if (archetype.icon) {
    const icon = createSpriteLabel(archetype.icon, color, 0.8);
    icon.position.y = radius + 1.2;
    group.add(icon);
  }

  group.userData = {
    id: archetype.id,
    type: 'archetype',
    label: archetype.label,
    description: archetype.description,
    position: archetype.position,
    color: color,
    mesh,
    halo,
    ring,
    ring2,
    baseScale: 1,
    colorDirty: false,
  };

  return group;
};

// ═══════════════════════════════════════════════════════════════════════════
// ACTEURS — MATÉRIAUX PREMIUM
// ═══════════════════════════════════════════════════════════════════════════

const createActor = (actor, config, perf) => {
  const group = new THREE.Group();
  const position = positionTo3D(actor.position, config);
  group.position.copy(position);

  const color = actor.color || '#00e5ff';
  const radius = actor.type === 'self' ? 0.3 : 0.25;

  const geo = new THREE.IcosahedronGeometry(radius, perf.icoDetailActor);

  const mat = new THREE.MeshPhysicalMaterial({
    color: color,
    emissive: color,
    emissiveIntensity: 0.6,
    metalness: 0.3,
    roughness: 0.35,
    transparent: true,
    opacity: 0.95,
    ior: 1.4,
    thickness: 0.3,
    clearcoat: 0.6,
    clearcoatRoughness: 0.2,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow = perf.shadowsEnabled;
  mesh.receiveShadow = perf.shadowsEnabled;
  mesh.userData.baseEmissive = 0.6;
  group.add(mesh);

  if (actor.type === 'self') {
    // Halo pour le self plus prononcé
    const haloGeo = new THREE.SphereGeometry(radius * 2.2, 20, 20);
    const haloMat = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    group.add(halo);
    group.userData.halo = halo;

    // Anneau pour le self
    const selfRingGeo = new THREE.TorusGeometry(radius * 1.6, 0.015, 10, 128);
    const selfRingMat = new THREE.MeshStandardMaterial({
      color: color,
      metalness: 0.5,
      roughness: 0.3,
      emissive: color,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.6,
    });
    const selfRing = new THREE.Mesh(selfRingGeo, selfRingMat);
    selfRing.castShadow = perf.shadowsEnabled;
    group.add(selfRing);
  }

  const label = createSpriteLabel(actor.label, color, 1.1);
  label.position.y = radius + 0.6;
  group.add(label);

  group.userData = {
    id: actor.id,
    type: actor.type === 'self' ? 'self' : 'actor',
    label: actor.label,
    position: actor.position,
    color: color,
    mesh,
    editable: actor.editable,
  };

  return group;
};

// ═══════════════════════════════════════════════════════════════════════════════
// TRAJECTOIRES
// ═══════════════════════════════════════════════════════════════════════════════

const createTrajectory = (trajectory, archetypesMap, config) => {
  const from = archetypesMap.get(trajectory.from);
  const to = archetypesMap.get(trajectory.to);
  if (!from || !to) return null;

  const start = from.group.position.clone();
  const end = to.group.position.clone();

  const midPoint = new THREE.Vector3(
    (start.x + end.x) / 2,
    Math.max(start.y, end.y) + 3,
    (start.z + end.z) / 2
  );

  const curve = new THREE.QuadraticBezierCurve3(start, midPoint, end);
  const points = curve.getPoints(40);

  const geo = new THREE.BufferGeometry().setFromPoints(points);
  const mat = new THREE.LineDashedMaterial({
    color: trajectory.color || C.violet,
    dashSize: 0.3,
    gapSize: 0.2,
    transparent: true,
    opacity: 0.5,
  });
  const line = new THREE.Line(geo, mat);
  line.computeLineDistances();

  line.userData = {
    id: trajectory.id,
    type: 'trajectory',
    label: trajectory.label,
    description: trajectory.description,
    color: trajectory.color,
  };

  return { line, curve };
};

// Marqueur de flux — petite sphère lumineuse qui parcourt la courbe de la
// trajectoire en continu, pour rendre visible le SENS (from → to) que le
// simple pointillé statique ne montrait pas.
const createFlowMarker = (trajectory) => {
  const color = trajectory.color || C.violet;
  const geo = new THREE.SphereGeometry(0.06, 12, 12);
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.95,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const marker = new THREE.Mesh(geo, mat);

  const haloGeo = new THREE.SphereGeometry(0.16, 12, 12);
  const haloMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.35,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const halo = new THREE.Mesh(haloGeo, haloMat);
  marker.add(halo);
  marker.userData.baseColor = color;

  return marker;
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCÈNE 3D — RENDERER
// ═══════════════════════════════════════════════════════════════════════════════

const PosturesSceneRenderer = ({
  config,
  positions,
  hoveredTarget,
  setHoveredTarget,
  onItemClick,
  showAxes = true,
  editMode = 'view',
  activeScenario = null,
  previewScenario = null,
  activeDomain = null,
  onActorDragStart,
  onActorDragEnd,
}) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rafRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const hoveredTargetRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());
  const cameraStateRef = useRef(null);
  const perfRef = useRef(null);
  if (!perfRef.current) perfRef.current = PERF_PROFILES[detectPerfTier()];

  const archetypesMapRef = useRef(new Map());
  const actorsMapRef = useRef(new Map());
  const trajectoriesRef = useRef([]);
  const axisObjectsRef = useRef([]);

  const editModeRef = useRef(editMode);
  const showAxesRef = useRef(showAxes);
  const activeScenarioRef = useRef(activeScenario);
  const previewScenarioRef = useRef(previewScenario);
  const activeDomainRef = useRef(activeDomain);
  const prevScenarioRef = useRef(activeScenario);

  const dragStateRef = useRef({
    isDragging: false,
    actorId: null,
    offset: new THREE.Vector3(),
    movedDistance: 0,
    startMouse: new THREE.Vector2(),
  });

  useEffect(() => { editModeRef.current = editMode; }, [editMode]);
  useEffect(() => { hoveredTargetRef.current = hoveredTarget; }, [hoveredTarget]);
  useEffect(() => { showAxesRef.current = showAxes; }, [showAxes]);
  useEffect(() => {
    activeScenarioRef.current = activeScenario;
    // Un changement de scénario est le seul moment où les couleurs des
    // archétypes doivent recommencer à être interpolées ; on marque tout le
    // monde "dirty" une seule fois plutôt que de lerper en continu.
    archetypesMapRef.current.forEach(({ group }) => {
      group.userData.colorDirty = true;
    });
  }, [activeScenario]);
  useEffect(() => {
    previewScenarioRef.current = previewScenario;
    // Le survol du radar (preview) doit recolorer immédiatement, y compris
    // pour revenir à l'état "activeScenario" (ou neutre) quand on quitte le
    // survol — donc on remet colorDirty à chaque changement, entrée ou sortie.
    archetypesMapRef.current.forEach(({ group }) => {
      group.userData.colorDirty = true;
    });
  }, [previewScenario]);
  useEffect(() => { activeDomainRef.current = activeDomain; }, [activeDomain]);

  // ═══════════════════════════════════════════════════════════════════════════
  // EFFET 1 : Création de la scène
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !config) return;
    const perf = perfRef.current;

    let mounted = false;
    let resizeObserver = null;
    let rafId = null;

    if (sceneRef.current) {
      cancelAnimationFrame(rafRef.current);
      sceneRef.current.dispose?.();
      sceneRef.current = null;
    }

    archetypesMapRef.current.clear();
    actorsMapRef.current.clear();
    trajectoriesRef.current = [];
    axisObjectsRef.current = [];

    const tryInit = () => {
      if (mounted) return;
      const W = container.clientWidth;
      const H = container.clientHeight;
      if (W === 0 || H === 0) return;

      const sceneResult = createPosturesScene(container, config, perf);
      if (!sceneResult) return;

      mounted = true;
      const { scene, camera, renderer, controls, composer } = sceneResult;

      if (cameraStateRef.current) {
        camera.position.copy(cameraStateRef.current.position);
        controls.target.copy(cameraStateRef.current.target);
        controls.update();
      }

      axisObjectsRef.current = createAxisRepresentations(scene, config);

      config.archetypes?.forEach(arch => {
        const group = createArchetype(arch, config, perf);
        scene.add(group);
        archetypesMapRef.current.set(arch.id, { group, data: arch });
      });

      config.actors?.forEach(actor => {
        const dynamicPosition = positions?.[actor.id] || actor.position;
        const actorWithPos = { ...actor, position: dynamicPosition };
        const group = createActor(actorWithPos, config, perf);
        group.userData.targetPosition = positionTo3D(dynamicPosition, config);
        scene.add(group);
        actorsMapRef.current.set(actor.id, { group, data: actorWithPos });
      });

      config.trajectories?.forEach((traj, trajIndex) => {
        const result = createTrajectory(traj, archetypesMapRef.current, config);
        if (result) {
          const { line, curve } = result;
          scene.add(line);
          const flowMarker = createFlowMarker(traj);
          scene.add(flowMarker);
          trajectoriesRef.current.push({
            line,
            curve,
            marker: flowMarker,
            phase: trajIndex / Math.max(1, config.trajectories.length),
          });
        }
      });

      const updateMouseFromEvent = (event) => {
        const rect = container.getBoundingClientRect();
        mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      };

      // ── Handlers souris ──
      const handleMouseDown = (event) => {
        if (editModeRef.current !== 'drag') return;
        if (event.button !== 0) return;

        updateMouseFromEvent(event);
        raycasterRef.current.setFromCamera(mouseRef.current, camera);

        const actorTargets = Array.from(actorsMapRef.current.values()).map(v => v.group);
        const intersects = raycasterRef.current.intersectObjects(actorTargets, true);

        if (intersects.length > 0) {
          let obj = intersects[0].object;
          while (obj.parent && !obj.userData?.id) obj = obj.parent;
          const actorId = obj.userData?.id;

          if (actorId) {
            dragStateRef.current.isDragging = true;
            dragStateRef.current.actorId = actorId;
            dragStateRef.current.offset.copy(intersects[0].point).sub(obj.position);
            dragStateRef.current.movedDistance = 0;
            dragStateRef.current.startMouse.set(event.clientX, event.clientY);
            controls.enabled = false;
            container.style.cursor = 'grabbing';
            if (onActorDragStart) onActorDragStart(actorId);
          }
        }
      };

      const handleMouseMove = (event) => {
        updateMouseFromEvent(event);
        raycasterRef.current.setFromCamera(mouseRef.current, camera);

        if (dragStateRef.current.isDragging) {
          dragStateRef.current.movedDistance += Math.sqrt(
            Math.pow(event.clientX - dragStateRef.current.startMouse.x, 2) +
            Math.pow(event.clientY - dragStateRef.current.startMouse.y, 2)
          );
          dragStateRef.current.startMouse.set(event.clientX, event.clientY);

          const actor = actorsMapRef.current.get(dragStateRef.current.actorId);
          if (actor) {
            const plane = new THREE.Plane();
            const normal = camera.getWorldDirection(new THREE.Vector3());
            plane.setFromNormalAndCoplanarPoint(normal, actor.group.position);
            const intersection = new THREE.Vector3();
            if (raycasterRef.current.ray.intersectPlane(plane, intersection)) {
              intersection.sub(dragStateRef.current.offset);
              actor.group.position.copy(intersection);
              actor.group.userData.targetPosition = intersection.clone();
            }
          }
          return;
        }

        const targets = [
          ...Array.from(archetypesMapRef.current.values()).map(v => v.group),
          ...Array.from(actorsMapRef.current.values()).map(v => v.group),
        ];
        const intersects = raycasterRef.current.intersectObjects(targets, true);

        if (intersects.length > 0) {
          let obj = intersects[0].object;
          while (obj.parent && !obj.userData?.id) obj = obj.parent;
          const id = obj.userData?.id;

          if (id && id !== hoveredTargetRef.current) {
            hoveredTargetRef.current = id;
            setHoveredTarget?.(id);
            const isActor = actorsMapRef.current.has(id);
            container.style.cursor = (editModeRef.current === 'drag' && isActor) ? 'grab' : 'pointer';
          }
        } else {
          if (hoveredTargetRef.current) {
            hoveredTargetRef.current = null;
            setHoveredTarget?.(null);
          }
          container.style.cursor = editModeRef.current === 'drag' ? 'grab' : 'default';
        }
      };

      const handleMouseUp = () => {
        if (!dragStateRef.current.isDragging) return;
        const actorId = dragStateRef.current.actorId;
        const wasRealDrag = dragStateRef.current.movedDistance > 5;
        dragStateRef.current.isDragging = false;
        dragStateRef.current.actorId = null;
        dragStateRef.current.movedDistance = 0;
        controls.enabled = true;
        container.style.cursor = editModeRef.current === 'drag' ? 'grab' : 'default';

        if (wasRealDrag && onActorDragEnd && actorId) {
          const actor = actorsMapRef.current.get(actorId);
          if (actor) onActorDragEnd(actorId, actor.group.position.clone());
        }
      };

      const handleClick = (event) => {
        if (dragStateRef.current.movedDistance > 5) return;
        updateMouseFromEvent(event);
        raycasterRef.current.setFromCamera(mouseRef.current, camera);

        const targets = [
          ...Array.from(archetypesMapRef.current.values()).map(v => v.group),
          ...Array.from(actorsMapRef.current.values()).map(v => v.group),
        ];
        const intersects = raycasterRef.current.intersectObjects(targets, true);

        if (intersects.length > 0) {
          let obj = intersects[0].object;
          while (obj.parent && !obj.userData?.id) obj = obj.parent;
          const userData = obj.userData;
          if (userData?.id && onItemClick) onItemClick(userData);
        }
      };

      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseup', handleMouseUp);
      container.addEventListener('click', handleClick);
      window.addEventListener('mouseup', handleMouseUp);

      // ── Animation ──
      // Boucle optimisée :
      //  - les couleurs d'archétype ne sont ré-interpolées que si colorDirty
      //    (posé au changement de scénario) — jusqu'à convergence, puis figées.
      //  - le hover ne fait plus qu'un halo + un léger boost d'emissive/scale,
      //    beaucoup plus lisible qu'avant sans coût de geometry supplémentaire.
      let frameCount = 0;
      const animate = () => {
        rafRef.current = requestAnimationFrame(animate);
        frameCount++;
        const time = clockRef.current.getElapsedTime();
        const currentHover = hoveredTargetRef.current;
        // Le scénario "preview" (survol du radar) prend le dessus sur le
        // scénario actif du mode Scénario — c'est une lecture temporaire, pas
        // une navigation, donc elle ne remplace jamais l'état persistant.
        const effectiveScenarioId = previewScenarioRef.current || activeScenarioRef.current;
        const scenarioData = effectiveScenarioId
          ? config.scenarios?.find(s => s.id === effectiveScenarioId)
          : null;

        // Archétypes
        archetypesMapRef.current.forEach(({ group, data }, id) => {
          const isHovered = currentHover === id;
          const halo = group.userData.halo;
          const ring = group.userData.ring;
          const ring2 = group.userData.ring2;
          const mesh = group.userData.mesh;

          if (group.userData.colorDirty) {
            let targetColor = data.color;
            if (scenarioData) {
              const isCritical = scenarioData.criticalPostures?.includes(id);
              const isDurable = scenarioData.durablePostures?.includes(id);
              if (isCritical) targetColor = C.red;
              else if (isDurable) targetColor = C.green;
            }
            const target = new THREE.Color(targetColor);
            let stillMoving = false;

            if (mesh?.material) {
              if (mesh.material.color.getHexString() !== target.getHexString()) {
                mesh.material.color.lerp(target, 0.06);
                mesh.material.emissive.lerp(target, 0.06);
                stillMoving = true;
              }
            }
            if (halo?.material && halo.material.color.getHexString() !== target.getHexString()) {
              halo.material.color.lerp(target, 0.06);
              stillMoving = true;
            }
            if (ring?.material && ring.material.color.getHexString() !== target.getHexString()) {
              ring.material.color.lerp(target, 0.06);
              stillMoving = true;
            }
            if (!stillMoving) group.userData.colorDirty = false;
          }

          if (halo) {
            const pulse = Math.sin(time * 1.5) * 0.15;
            const scenarioBoost = scenarioData
              ? (scenarioData.criticalPostures?.includes(id) || scenarioData.durablePostures?.includes(id)) ? 0.2 : 0
              : 0;
            const hoverBoost = isHovered ? 0.35 : 0;
            halo.scale.setScalar(1 + pulse + hoverBoost + scenarioBoost);
            halo.material.opacity = 0.12 + pulse * 0.1 + (isHovered ? 0.18 : 0) + scenarioBoost * 0.3;
          }

          // Meilleure visibilité au survol : léger boost d'émissivité + échelle
          if (mesh?.material) {
            const targetEmissive = isHovered
              ? mesh.userData.baseEmissive + 0.35
              : mesh.userData.baseEmissive;
            mesh.material.emissiveIntensity += (targetEmissive - mesh.material.emissiveIntensity) * 0.15;
          }
          const targetScale = isHovered ? 1.15 : 1;
          group.scale.x += (targetScale - group.scale.x) * 0.18;
          group.scale.y = group.scale.z = group.scale.x;

          if (ring) {
            ring.rotation.z += 0.002;
            ring.rotation.x = Math.PI / 3 + Math.sin(time * 0.5) * 0.1;
          }
          if (ring2) {
            ring2.rotation.z -= 0.0014;
          }
        });

        // Acteurs
        actorsMapRef.current.forEach(({ group }, id) => {
          const isHovered = currentHover === id;
          const halo = group.userData.halo;
          const mesh = group.userData.mesh;
          const isBeingDragged =
            dragStateRef.current.isDragging &&
            dragStateRef.current.actorId === id;

          if (!isBeingDragged && group.userData.targetPosition) {
            group.position.lerp(group.userData.targetPosition, 0.08);
          }

          if (halo) {
            const pulse = Math.sin(time * 2) * 0.2;
            const dragBoost = isBeingDragged ? 0.5 : 0;
            halo.scale.setScalar(1 + pulse + (isHovered ? 0.4 : 0) + dragBoost);
            halo.material.opacity = 0.25 + pulse * 0.15 + (isHovered ? 0.2 : 0) + dragBoost * 0.2;
          }
          if (mesh?.material) {
            const targetEmissive = isHovered
              ? mesh.userData.baseEmissive + 0.3
              : mesh.userData.baseEmissive;
            mesh.material.emissiveIntensity += (targetEmissive - mesh.material.emissiveIntensity) * 0.15;
          }
        });

        // Axes — pas de calcul par frame, juste application de l'état courant
        if (frameCount % 2 === 0) {
          axisObjectsRef.current.forEach(obj => {
            if (obj.material) {
              const base = obj.userData.baseOpacity ?? 1;
              obj.material.opacity = showAxesRef.current ? base : 0;
            }
          });
        }

        // Flux directionnel des trajectoires — un point lumineux parcourt
        // chaque courbe en boucle (from → to), déphasé par trajectoire pour
        // éviter un effet de pulsation synchrone.
        const FLOW_PERIOD = 4.5;
        trajectoriesRef.current.forEach(traj => {
          if (!traj.curve || !traj.marker) return;
          const t = ((time / FLOW_PERIOD + traj.phase) % 1 + 1) % 1;
          const point = traj.curve.getPointAt(t);
          traj.marker.position.copy(point);
          const pulse = 0.85 + Math.sin(time * 3 + traj.phase * 10) * 0.15;
          traj.marker.scale.setScalar(pulse);
        });

        // Pendant un glissement, on gèle totalement la caméra. Sans ce
        // garde-fou, controls.enabled=false empêche bien les NOUVELLES
        // interactions orbite/zoom, mais n'arrête pas l'inertie résiduelle de
        // l'amortissement (enableDamping) si l'utilisateur venait d'orbiter
        // juste avant de saisir la sphère — controls.update() continuerait
        // alors à faire tourner légèrement la caméra à chaque frame, ce qui
        // fait pivoter sous le curseur le plan invisible sur lequel on glisse
        // (son normal = direction de la caméra) : la sphère "dérive" et ne
        // suit plus fidèlement le curseur, sans qu'aucun bug ne soit visible
        // dans le calcul du glissement lui-même.
        if (!dragStateRef.current.isDragging) {
          controls.update();
        }
        composer.render();
      };

      animate();

      // Redimensionnement — même approche que la version d'origine :
      // uniquement l'event 'resize' de la fenêtre (déclenché manuellement par
      // le composant parent après les animations Framer Motion), pas de
      // ResizeObserver posé sur le conteneur du canvas. C'est cet observer,
      // ajouté dans une itération précédente, qui provoquait le warning
      // "ResizeObserver loop completed with undelivered notifications" :
      // le canvas qu'il observait changeait de taille en réaction à son
      // propre callback pendant l'animation de largeur du panneau latéral.
      let pendingResize = false;
      const scheduleResize = () => {
        if (pendingResize) return;
        pendingResize = true;
        requestAnimationFrame(() => {
          pendingResize = false;

          const nw = container.clientWidth;
          const nh = container.clientHeight;
          if (nw === 0 || nh === 0) return;

          const currentW = renderer.domElement.width / renderer.getPixelRatio();
          const currentH = renderer.domElement.height / renderer.getPixelRatio();
          if (Math.abs(nw - currentW) < 0.5 && Math.abs(nh - currentH) < 0.5) return;

          camera.aspect = nw / nh;
          camera.updateProjectionMatrix();
          renderer.setSize(nw, nh, false);
          composer.setSize(nw * perf.bloomResolutionScale, nh * perf.bloomResolutionScale);
          renderer.setViewport(0, 0, nw, nh);
        });
      };

      window.addEventListener('resize', scheduleResize);

      const initResizeRaf = requestAnimationFrame(() => {
        requestAnimationFrame(scheduleResize);
      });

      // ── Cleanup ──
      const cleanup = () => {
        cancelAnimationFrame(rafRef.current);
        cancelAnimationFrame(initResizeRaf);
        window.removeEventListener('resize', scheduleResize);

        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
        container.removeEventListener('click', handleClick);
        window.removeEventListener('mouseup', handleMouseUp);

        cameraStateRef.current = {
          position: camera.position.clone(),
          target: controls.target.clone(),
        };

        scene.traverse(obj => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
            else obj.material.dispose();
          }
        });

        controls.dispose();
        composer.dispose();
        renderer.dispose();

        if (renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }

        axisObjectsRef.current = [];
        archetypesMapRef.current.clear();
        actorsMapRef.current.clear();
        trajectoriesRef.current = [];
        sceneRef.current = null;
        mounted = false;
      };

      sceneRef.current = { scene, camera, renderer, controls, composer, dispose: cleanup };
    };

    rafId = requestAnimationFrame(() => {
      tryInit();

      if (!mounted) {
        resizeObserver = new ResizeObserver(() => {
          tryInit();
          if (mounted && resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
          }
        });
        resizeObserver.observe(container);
      }
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      if (sceneRef.current?.dispose) sceneRef.current.dispose();
    };
  }, [config]);

  // ═══════════════════════════════════════════════════════════════════════════
  // EFFET 2 : Positions
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!sceneRef.current || !positions) return;
    Object.entries(positions).forEach(([actorId, position]) => {
      const actor = actorsMapRef.current.get(actorId);
      if (!actor) return;
      const isBeingDragged =
        dragStateRef.current.isDragging &&
        dragStateRef.current.actorId === actorId;
      if (isBeingDragged) return;
      actor.group.userData.targetPosition = positionTo3D(position, config);
    });
  }, [positions, config]);

  return <div ref={containerRef} className="w-full h-full" style={{
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    minHeight: 0,
  }}/>;
};

const HelpButton = ({ onClick, color = C.amber, title = 'Ouvrir le livret' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    title={title}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 18,
      height: 18,
      background: 'transparent',
      border: `1px solid ${C.border}`,
      borderRadius: 3,
      cursor: 'pointer',
      color,
      padding: 0,
      flexShrink: 0,
      transition: 'all 0.15s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.color = color;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = C.border;
      e.currentTarget.style.color = color;
    }}
  >
    <HelpCircle size={10} />
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════════
// RADAR DE SCÉNARIOS — lecture passive
// ═══════════════════════════════════════════════════════════════════════════════

const ScenarioRadar = ({ scenarios, config, selfPosition, onHoverScenario, onOpenScenario, hoveredScenarioId }) => {
  const rows = useMemo(() => {
    return (scenarios || []).map(scenario => ({
      scenario,
      result: getClosestScenarioBehavior(config, scenario, selfPosition),
    }));
  }, [scenarios, config, selfPosition]);

  if (rows.length === 0) return null;

  const criticalCount = rows.filter(r => r.result?.behavior?.risk === 'critical').length;
  const durableCount = rows.filter(r => r.result?.behavior?.risk === 'low').length;

  return (
    <div style={{
      background: C.bgPanel + 'd8',
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      padding: '10px 12px',
      backdropFilter: 'blur(8px)',
      width: 236,
    }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <div className="flex items-center gap-2">
          <Target size={10} color={C.textAccent} />
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.textAccent, letterSpacing: '0.14em' }}>
            RADAR DE SCÉNARIOS
          </span>
        </div>
        <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted }}>
          {durableCount}✓ · {criticalCount}!
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {rows.map(({ scenario, result }) => {
          const risk = result?.behavior?.risk;
          const riskColor = RISK_COLOR[risk] || C.textMuted;
          const isHovered = hoveredScenarioId === scenario.id;

          return (
            <button
              key={scenario.id}
              onMouseEnter={() => onHoverScenario?.(scenario.id)}
              onMouseLeave={() => onHoverScenario?.(null)}
              onFocus={() => onHoverScenario?.(scenario.id)}
              onBlur={() => onHoverScenario?.(null)}
              onClick={() => onOpenScenario?.(scenario)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '4px 6px',
                background: isHovered ? riskColor + '15' : 'transparent',
                border: `1px solid ${isHovered ? riskColor + '40' : 'transparent'}`,
                borderRadius: 3,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s, border-color 0.12s',
              }}
            >
              <div style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                flexShrink: 0,
                background: riskColor,
                boxShadow: isHovered ? `0 0 6px ${riskColor}` : 'none',
              }} />
              <span style={{
                fontFamily: MONO,
                fontSize: 8.5,
                color: isHovered ? C.textPrimary : C.textSecond,
                flex: 1,
                lineHeight: 1.3,
              }}>
                {scenario.shortLabel || scenario.label}
              </span>
              {result?.archetype && (
                <span style={{ fontFamily: MONO, fontSize: 9, color: result.archetype.color, flexShrink: 0 }}>
                  {result.archetype.icon}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — ARCHÉTYPE
// ═══════════════════════════════════════════════════════════════════════════════

const ArchetypeCard = ({ archetype, onClose, config, activeDomain, onOpenLivret }) => {
  const [tab, setTab] = useState('essence');
  if (!archetype) return null;
  const theoryRefs = archetype.theoryRefs || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${archetype.color}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${archetype.color}, transparent)` }} />

      <div style={{ padding: '12px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 4 }}>
              ARCHÉTYPE
            </div>
            <div className="flex items-center gap-2">
              {archetype.icon && (
                <span style={{ fontFamily: MONO, fontSize: 16, color: archetype.color }}>
                  {archetype.icon}
                </span>
              )}
              <div style={{ fontFamily: DISPLAY, fontSize: 15, color: C.textPrimary, fontWeight: 600 }}>
                {archetype.label}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <HelpButton onClick={() => onOpenLivret?.('archetype')} />
             <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
              <X size={12} />
             </button>
        </div>
        </div>

        {archetype.signature && (
          <div style={{
            marginTop: 8,
            padding: '6px 10px',
            background: archetype.color + '08',
            borderLeft: `2px solid ${archetype.color}`,
            borderRadius: '0 4px 4px 0',
            fontFamily: BODY,
            fontSize: 11,
            fontStyle: 'italic',
            color: C.textPrimary,
            lineHeight: 1.6,
          }}>
            « {archetype.signature} »
          </div>
        )}
      </div>

      <div className="flex" style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        {[
          { id: 'essence', label: 'ESSENCE' },
          { id: 'produit', label: 'PRODUIT' },
          { id: 'angles', label: 'ANGLES MORTS' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1,
              padding: '6px 0',
              fontFamily: MONO,
              fontSize: 8,
              letterSpacing: '0.1em',
              color: tab === t.id ? archetype.color : C.textMuted,
              borderBottom: `1px solid ${tab === t.id ? archetype.color : 'transparent'}`,
              background: tab === t.id ? archetype.color + '10' : 'transparent',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '10px 12px' }}>
        <AnimatePresence mode="wait">
          {tab === 'essence' && (
            <motion.div key="essence" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond, lineHeight: 1.7, margin: '0 0 10px 0' }}>
                {archetype.description}
              </p>
              {archetype.phenomenology && (
                <div style={{
                  padding: 8,
                  background: C.bgInset,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                }}>
                  <div style={{ fontFamily: MONO, fontSize: 7, color: archetype.color, letterSpacing: '0.12em', marginBottom: 4 }}>
                    PHÉNOMÉNOLOGIE
                  </div>
                  <p style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary, lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                    {archetype.phenomenology}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {tab === 'produit' && (
            <motion.div key="produit" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {archetype.outcomes?.length > 0 && (
                <div style={{
                  marginBottom: 10,
                  padding: 8,
                  background: C.greenFaint,
                  border: `1px solid ${C.green}25`,
                  borderRadius: 4,
                }}>
                  <div style={{ fontFamily: MONO, fontSize: 7, color: C.green, letterSpacing: '0.12em', marginBottom: 6 }}>
                    CE QUE ÇA PRODUIT
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {archetype.outcomes.map(o => (
                      <span key={o} style={{
                        fontFamily: MONO,
                        fontSize: 9,
                        color: C.textPrimary,
                        background: C.bgInset,
                        border: `1px solid ${C.border}`,
                        borderRadius: 3,
                        padding: '2px 6px',
                      }}>
                        {o}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {archetype.domains?.length > 0 && (
                <div>
                  <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 4 }}>
                    DOMAINES OÙ CETTE POSTURE EST FRÉQUENTE
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {archetype.domains.map(d => {
                      const domainData = config.domains?.find(dm => dm.id === d);
                      const isActive = activeDomain === d;
                      return (
                        <span key={d} style={{
                          fontFamily: MONO,
                          fontSize: 9,
                          color: isActive ? (domainData?.color || C.textPrimary) : C.textMuted,
                          background: isActive ? (domainData?.color + '15') : C.bgInset,
                          border: `1px solid ${isActive ? (domainData?.color + '60') : C.border}`,
                          borderRadius: 3,
                          padding: '2px 6px',
                        }}>
                          {domainData?.label || d}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {tab === 'angles' && (
            <motion.div key="angles" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {archetype.blindspots?.length > 0 ? (
                <div style={{
                  padding: 8,
                  background: C.redFaint,
                  border: `1px solid ${C.red}25`,
                  borderRadius: 4,
                }}>
                  <div style={{ fontFamily: MONO, fontSize: 7, color: C.red, letterSpacing: '0.12em', marginBottom: 6 }}>
                    CE QUE CETTE POSTURE NE VOIT PAS
                  </div>
                  {archetype.blindspots.map(b => (
                    <div key={b} style={{
                      fontFamily: BODY,
                      fontSize: 11,
                      color: '#cc8888',
                      lineHeight: 1.6,
                      marginBottom: 4,
                      display: 'flex',
                      gap: 6,
                    }}>
                      <span style={{ color: C.red, flexShrink: 0 }}>·</span>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontFamily: BODY, fontSize: 11, color: C.textMuted, textAlign: 'center', padding: 8 }}>
                  Aucun angle mort identifié
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {theoryRefs.length > 0 && config?.theoryRefs && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 6 }}>
              RÉFÉRENCES
            </div>
            {theoryRefs.map(refId => {
              const ref = config.theoryRefs[refId];
              if (!ref) return null;
              return (
                <div key={refId} style={{ marginBottom: 6 }}>
                  <div style={{ fontFamily: BODY, fontSize: 10, color: C.textPrimary, fontWeight: 500 }}>
                    {ref.label}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, marginTop: 1 }}>
                    {ref.author} · {ref.year}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — TRAJECTOIRE
// ═══════════════════════════════════════════════════════════════════════════════

const TrajectoryCard = ({ trajectory, onClose, config, onOpenLivret }) => {
  if (!trajectory) return null;
  const fromArch = config.archetypes?.find(a => a.id === trajectory.from);
  const toArch = config.archetypes?.find(a => a.id === trajectory.to);
  const theoryRefs = trajectory.theoryRefs || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${trajectory.color}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${trajectory.color}, transparent)` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
              TRAJECTOIRE
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 14, color: C.textPrimary, fontWeight: 600 }}>
              {trajectory.label}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <HelpButton onClick={() => onOpenLivret?.('trajectory')} />
            <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
                <X size={12} />
            </button>
        </div>
        </div>

        {fromArch && toArch && (
          <div className="flex items-center gap-2" style={{
            marginTop: 6,
            padding: '6px 8px',
            background: C.bgInset,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
          }}>
            <span style={{ fontFamily: MONO, fontSize: 10, color: fromArch.color }}>
              {fromArch.icon} {fromArch.shortLabel}
            </span>
            <ArrowRight size={12} color={trajectory.color} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: toArch.color }}>
              {toArch.icon} {toArch.shortLabel}
            </span>
            {trajectory.duration && (
              <>
                <span style={{ flex: 1 }} />
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted }}>
                  {trajectory.duration}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        <p style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond, lineHeight: 1.7, margin: '0 0 10px 0' }}>
          {trajectory.description}
        </p>

        {trajectory.stages?.length > 0 && (
          <div style={{
            marginBottom: 10,
            padding: 8,
            background: trajectory.color + '08',
            border: `1px solid ${trajectory.color}25`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: trajectory.color, letterSpacing: '0.12em', marginBottom: 6 }}>
              ÉTAPES
            </div>
            {trajectory.stages.map((stage, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: i < trajectory.stages.length - 1 ? 6 : 0 }}>
                <div style={{ fontFamily: MONO, fontSize: 9, color: trajectory.color, width: 16, flexShrink: 0 }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary, fontWeight: 500 }}>
                    {stage.name}
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 10, color: C.textSecond, lineHeight: 1.5 }}>
                    {stage.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {trajectory.conditions?.length > 0 && (
          <div style={{
            marginBottom: 8,
            padding: 8,
            background: C.greenFaint,
            border: `1px solid ${C.green}25`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.green, letterSpacing: '0.12em', marginBottom: 6 }}>
              CONDITIONS
            </div>
            {trajectory.conditions.map(c => (
              <div key={c} style={{
                fontFamily: BODY,
                fontSize: 11,
                color: C.textPrimary,
                lineHeight: 1.6,
                marginBottom: 3,
                display: 'flex',
                gap: 6,
              }}>
                <span style={{ color: C.green, flexShrink: 0 }}>+</span>
                <span>{c}</span>
              </div>
            ))}
          </div>
        )}

        {trajectory.obstacles?.length > 0 && (
          <div style={{
            padding: 8,
            background: C.redFaint,
            border: `1px solid ${C.red}25`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.red, letterSpacing: '0.12em', marginBottom: 6 }}>
              OBSTACLES
            </div>
            {trajectory.obstacles.map(o => (
              <div key={o} style={{
                fontFamily: BODY,
                fontSize: 11,
                color: '#cc8888',
                lineHeight: 1.6,
                marginBottom: 3,
                display: 'flex',
                gap: 6,
              }}>
                <span style={{ color: C.red, flexShrink: 0 }}>−</span>
                <span>{o}</span>
              </div>
            ))}
          </div>
        )}

        {theoryRefs.length > 0 && config?.theoryRefs && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 6 }}>
              RÉFÉRENCES
            </div>
            {theoryRefs.map(refId => {
              const ref = config.theoryRefs[refId];
              if (!ref) return null;
              return (
                <div key={refId} style={{ marginBottom: 6 }}>
                  <div style={{ fontFamily: BODY, fontSize: 10, color: C.textPrimary, fontWeight: 500 }}>
                    {ref.label}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, marginTop: 1 }}>
                    {ref.author} · {ref.year}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — AXE
// ═══════════════════════════════════════════════════════════════════════════════

const AxisCard = ({ axis, onClose, onOpenLivret }) => {
  if (!axis) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${C.border}`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${axis.poleA.color}, ${axis.poleB.color})` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
              AXE
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 14, color: C.textPrimary, fontWeight: 600 }}>
              {axis.label}
            </div>
          </div>
          <div className="flex items-center gap-1">
  <HelpButton onClick={() => onOpenLivret?.('axis')} />
  <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
    <X size={12} />
  </button>
</div>         
        </div>

        {axis.epistemologicalQuestion && (
          <div style={{
            marginTop: 8,
            padding: '8px 10px',
            background: C.violetFaint,
            border: `1px solid ${C.violet}25`,
            borderRadius: 4,
            fontFamily: BODY,
            fontSize: 11,
            fontStyle: 'italic',
            color: C.textPrimary,
            lineHeight: 1.6,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.violet, letterSpacing: '0.12em', marginBottom: 4, fontStyle: 'normal' }}>
              QUESTION ÉPISTÉMOLOGIQUE
            </div>
            {axis.epistemologicalQuestion}
          </div>
        )}
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        {[axis.poleA, axis.poleB].map((pole, i) => (
          <div key={pole.id} style={{
            marginTop: i === 0 ? 0 : 10,
            padding: 8,
            background: pole.color + '08',
            border: `1px solid ${pole.color}25`,
            borderRadius: 4,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: pole.color,
                boxShadow: `0 0 6px ${pole.color}`,
              }} />
              <span style={{ fontFamily: MONO, fontSize: 10, color: pole.color, letterSpacing: '0.08em' }}>
                {pole.label.toUpperCase()}
              </span>
            </div>

            <p style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond, lineHeight: 1.6, margin: '0 0 6px 0' }}>
              {pole.description}
            </p>

            {pole.phenomenology && (
              <p style={{
                fontFamily: BODY,
                fontSize: 10,
                fontStyle: 'italic',
                color: C.textMuted,
                lineHeight: 1.6,
                margin: '0 0 6px 0',
                paddingLeft: 8,
                borderLeft: `2px solid ${pole.color}40`,
              }}>
                {pole.phenomenology}
              </p>
            )}

            {pole.consequences && (
              <div>
                <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 4 }}>
                  CONSÉQUENCES
                </div>
                {pole.consequences.court?.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ fontFamily: MONO, fontSize: 8, color: C.amber }}>Court terme · </span>
                    <span style={{ fontFamily: BODY, fontSize: 10, color: C.textPrimary }}>
                      {pole.consequences.court.join(', ')}
                    </span>
                  </div>
                )}
                {pole.consequences.long?.length > 0 && (
                  <div>
                    <span style={{ fontFamily: MONO, fontSize: 8, color: C.violet }}>Long terme · </span>
                    <span style={{ fontFamily: BODY, fontSize: 10, color: C.textPrimary }}>
                      {pole.consequences.long.join(', ')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — TENSION
// ═══════════════════════════════════════════════════════════════════════════════

const TensionCard = ({ tension, onClose, config, onOpenLivret }) => {
  if (!tension) return null;
  const axesInvolved = tension.between?.map(id => config.axes?.find(a => a.id === id)).filter(Boolean) || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${C.amber}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${C.amber}, transparent)` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div style={{ flex: 1 }}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={11} color={C.amber} />
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.amber, letterSpacing: '0.12em' }}>
                TENSION SYSTÉMIQUE
              </span>
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 13, color: C.textPrimary, fontWeight: 600, lineHeight: 1.3 }}>
              {axesInvolved.map(a => a.shortLabel).join('  ×  ')}
            </div>
          </div>
          <div className="flex items-center gap-1">
  <HelpButton onClick={() => onOpenLivret?.('tension')} />
  <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
    <X size={12} />
  </button>
</div>
        </div>
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        <p style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond, lineHeight: 1.7, margin: '0 0 10px 0' }}>
          {tension.description}
        </p>

        {tension.example && (
          <div style={{
            padding: 8,
            background: C.bgInset,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            marginBottom: 8,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 4 }}>
              EXEMPLE
            </div>
            <p style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary, lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
              {tension.example}
            </p>
          </div>
        )}

        {tension.resolution && (
          <div style={{
            padding: 8,
            background: C.greenFaint,
            border: `1px solid ${C.green}25`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.green, letterSpacing: '0.12em', marginBottom: 4 }}>
              VOIE DE RÉSOLUTION
            </div>
            <p style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary, lineHeight: 1.6, margin: 0 }}>
              {tension.resolution}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — PONT INTER-DOMAINES
// ═══════════════════════════════════════════════════════════════════════════════

const BridgeCard = ({ bridge, onClose, config, onOpenLivret }) => {
  if (!bridge) return null;
  const domainFrom = config.domains?.find(d => d.id === bridge.from);
  const domainTo = config.domains?.find(d => d.id === bridge.to);
  const sharedScenarios = (bridge.sharedScenarios || [])
    .map(id => config.scenarios?.find(s => s.id === id))
    .filter(Boolean);
  const sharedPostures = (config.domainMapping?.sharedPostures || [])
    .map(id => config.archetypes?.find(a => a.id === id))
    .filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${C.cyan}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${domainFrom?.color || C.cyan}, ${domainTo?.color || C.violet})` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
              PONT INTER-DOMAINES
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 14, color: C.textPrimary, fontWeight: 600 }}>
              {bridge.label}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <HelpButton onClick={() => onOpenLivret?.('bridge')} />
            <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
              <X size={12} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2" style={{
          marginTop: 6,
          padding: '6px 8px',
          background: C.bgInset,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
        }}>
          <span style={{ fontFamily: MONO, fontSize: 10, color: domainFrom?.color || C.textPrimary }}>
            {domainFrom?.label || bridge.from}
          </span>
          <ArrowRight size={12} color={C.textMuted} />
          <span style={{ fontFamily: MONO, fontSize: 10, color: domainTo?.color || C.textPrimary }}>
            {domainTo?.label || bridge.to}
          </span>
        </div>
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        <p style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond, lineHeight: 1.7, margin: '0 0 10px 0' }}>
          {bridge.description}
        </p>

        {sharedScenarios.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.cyan, letterSpacing: '0.12em', marginBottom: 6 }}>
              SCÉNARIOS PARTAGÉS
            </div>
            <div className="flex flex-wrap gap-1">
              {sharedScenarios.map(s => (
                <span key={s.id} style={{
                  fontFamily: MONO,
                  fontSize: 9,
                  color: C.textPrimary,
                  background: C.bgInset,
                  border: `1px solid ${C.border}`,
                  borderRadius: 3,
                  padding: '2px 6px',
                }}>
                  {s.shortLabel || s.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {sharedPostures.length > 0 && (
          <div>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 6 }}>
              POSTURES COMMUNES AUX DEUX MONDES
            </div>
            <div className="flex flex-wrap gap-1">
              {sharedPostures.map(a => (
                <span key={a.id} style={{
                  fontFamily: MONO,
                  fontSize: 9,
                  color: a.color,
                  background: C.bgInset,
                  border: `1px solid ${C.border}`,
                  borderRadius: 3,
                  padding: '2px 6px',
                }}>
                  {a.icon} {a.shortLabel}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — SIMULATION
// ═══════════════════════════════════════════════════════════════════════════════

const SimulationCard = ({ simulation, onClose, onOpenLivret }) => {
  if (!simulation) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${C.cyan}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${C.cyan}, transparent)` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div style={{ flex: 1 }}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={11} color={C.cyan} />
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.cyan, letterSpacing: '0.12em' }}>
                SIMULATION
              </span>
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 13, color: C.textPrimary, fontWeight: 600, lineHeight: 1.35 }}>
              {simulation.label}
            </div>
          </div>
          <div className="flex items-center gap-1">
  <HelpButton onClick={() => onOpenLivret?.('scenarioTest')} />
  <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
    <X size={12} />
  </button>
</div>
        </div>
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        {simulation.effects?.length > 0 && (
          <div style={{
            marginBottom: 10,
            padding: 8,
            background: C.cyanFaint,
            border: `1px solid ${C.cyan}25`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.cyan, letterSpacing: '0.12em', marginBottom: 6 }}>
              EFFETS
            </div>
            {simulation.effects.map((eff, i) => (
              <div key={i} style={{
                paddingBottom: i < simulation.effects.length - 1 ? 6 : 0,
                marginBottom: i < simulation.effects.length - 1 ? 6 : 0,
                borderBottom: i < simulation.effects.length - 1 ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontFamily: MONO, fontSize: 9, color: C.cyan }}>
                    {eff.domain.toUpperCase()}
                  </span>
                  {eff.timeHorizon && (
                    <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted }}>
                      {eff.timeHorizon}
                    </span>
                  )}
                </div>
                <div style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary, lineHeight: 1.6 }}>
                  {eff.change}
                </div>
              </div>
            ))}
          </div>
        )}

        {simulation.warnings?.length > 0 && (
          <div style={{
            padding: 8,
            background: C.amberFaint,
            border: `1px solid ${C.amber}25`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.amber, letterSpacing: '0.12em', marginBottom: 6 }}>
              À ANTICIPER
            </div>
            {simulation.warnings.map((w, i) => (
              <div key={i} style={{
                fontFamily: BODY,
                fontSize: 11,
                color: C.textSecond,
                lineHeight: 1.6,
                marginBottom: 3,
                display: 'flex',
                gap: 6,
              }}>
                <span style={{ color: C.amber, flexShrink: 0 }}>!</span>
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — SCÉNARIO
// ═══════════════════════════════════════════════════════════════════════════════

const ScenarioCard = ({ scenario, onClose, config, activeDomain, selfPosition, onOpenLivret }) => {
  if (!scenario) return null;

  const projection = activeDomain ? scenario.projections?.[activeDomain] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${C.orange}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${C.orange}, transparent)` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div style={{ flex: 1 }}>
            <div className="flex items-center gap-2 mb-2">
              <Target size={11} color={C.orange} />
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.orange, letterSpacing: '0.12em' }}>
                SCÉNARIO
              </span>
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 14, color: C.textPrimary, fontWeight: 600, lineHeight: 1.3 }}>
              {scenario.label}
            </div>
          </div>
          <div className="flex items-center gap-1">
  <HelpButton onClick={() => onOpenLivret?.('scenario')} />
  <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
    <X size={12} />
  </button>
</div>
        </div>
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        <p style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond, lineHeight: 1.7, margin: '0 0 10px 0' }}>
          {scenario.description}
        </p>

        {scenario.epistemologicalQuestion && (
          <div style={{
            marginBottom: 10,
            padding: '8px 10px',
            background: C.violetFaint,
            border: `1px solid ${C.violet}25`,
            borderRadius: 4,
            fontFamily: BODY,
            fontSize: 11,
            fontStyle: 'italic',
            color: C.textPrimary,
            lineHeight: 1.6,
          }}>
            {scenario.epistemologicalQuestion}
          </div>
        )}

        {projection && (
          <div style={{
            marginBottom: 10,
            padding: 8,
            background: C.bgInset,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 4 }}>
              PROJECTION — {activeDomain.toUpperCase()}
            </div>
            <div style={{ fontFamily: BODY, fontSize: 12, color: C.textPrimary, fontWeight: 500, marginBottom: 4 }}>
              {projection.label}
            </div>
            <div style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond, lineHeight: 1.6 }}>
              {projection.example}
            </div>
          </div>
        )}

        {scenario.behaviors && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 6 }}>
              COMPORTEMENTS PAR POSTURE
            </div>
            {Object.entries(scenario.behaviors).map(([archId, behavior]) => {
              const arch = config.archetypes?.find(a => a.id === archId);
              if (!arch) return null;

              const riskColor = behavior.risk === 'critical' ? C.red
                : behavior.risk === 'moderate' ? C.amber
                : C.green;

              return (
                <div key={archId} style={{
                  marginBottom: 6,
                  padding: 6,
                  background: C.bgInset,
                  border: `1px solid ${C.border}`,
                  borderLeft: `3px solid ${riskColor}`,
                  borderRadius: 3,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: arch.color }}>
                      {arch.icon}
                    </span>
                    <span style={{ fontFamily: MONO, fontSize: 9, color: arch.color, flex: 1 }}>
                      {arch.shortLabel}
                    </span>
                    <span style={{
                      fontFamily: MONO,
                      fontSize: 7,
                      color: riskColor,
                      background: riskColor + '15',
                      padding: '1px 4px',
                      borderRadius: 2,
                    }}>
                      {behavior.risk?.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 10, color: C.textSecond, lineHeight: 1.5, marginBottom: 2 }}>
                    {behavior.behavior}
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 10, color: C.textMuted, lineHeight: 1.5, fontStyle: 'italic' }}>
                    → {behavior.outcome}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selfPosition && (
          <div style={{
            padding: 8,
            background: C.cyanFaint,
            border: `1px solid ${C.cyan}25`,
            borderRadius: 4,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.cyan, letterSpacing: '0.12em', marginBottom: 6 }}>
              VOUS DANS CE SCÉNARIO
            </div>
            {(() => {
              let closest = null;
              let minDist = Infinity;
              config.archetypes?.forEach(a => {
                if (!scenario.behaviors?.[a.id]) return;
                const d = distance6D(selfPosition, a.position);
                if (d < minDist) { minDist = d; closest = a; }
              });
              if (!closest) return null;
              const behavior = scenario.behaviors[closest.id];
              const riskColor = behavior.risk === 'critical' ? C.red
                : behavior.risk === 'moderate' ? C.amber
                : C.green;

              return (
                <div>
                  <div style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary, marginBottom: 4 }}>
                    Votre posture la plus proche est <strong style={{ color: closest.color }}>{closest.label}</strong>.
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 10, color: C.textSecond, lineHeight: 1.6 }}>
                    Dans ce scénario, cela signifie : {behavior.behavior}
                  </div>
                  <div style={{
                    marginTop: 6,
                    fontFamily: MONO,
                    fontSize: 9,
                    color: riskColor,
                    letterSpacing: '0.08em',
                  }}>
                    RISQUE : {behavior.risk?.toUpperCase()}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CARTES UI — TRACE DE SESSION
// ═══════════════════════════════════════════════════════════════════════════════

const TraceCard = ({ trace, onClose, onSave, onDownload, onOpenLivret }) => {
  if (!trace) return null;

  const summary = summarizeTrace(trace);
  const integrity = verifyTrace(trace);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${C.cyan}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${C.cyan}, transparent)` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div style={{ flex: 1 }}>
            <div className="flex items-center gap-2 mb-2">
              <FileText size={11} color={C.cyan} />
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.cyan, letterSpacing: '0.12em' }}>
                TRACE DE SESSION
              </span>
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.textMuted, marginBottom: 2 }}>
              {summary.session_id}
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 13, color: C.textPrimary, fontWeight: 600 }}>
              {summary.actor}
            </div>
          </div>
          <div className="flex items-center gap-1">
  <HelpButton
    onClick={() => onOpenLivret?.('traceRead')}
    title="Lire une trace"
  />
  <HelpButton
    onClick={() => onOpenLivret?.('traceCertify')}
    title="Produire une preuve"
    color={C.amber}
  />
  <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
    <X size={12} />
  </button>
</div>
        </div>
      </div>

      <div style={{ padding: '0 12px 12px' }}>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {[
            { label: 'Référentiel', value: summary.referential },
            { label: 'Durée', value: summary.duration },
            { label: 'Scénarios', value: `${summary.scenarios.success}/${summary.scenarios.total}` },
            { label: 'Niveau', value: summary.level },
          ].map(row => (
            <div key={row.label} style={{
              padding: 6,
              background: C.bgInset,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
            }}>
              <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.08em', marginBottom: 2 }}>
                {row.label.toUpperCase()}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: C.textPrimary }}>
                {row.value || '—'}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          marginBottom: 10,
          padding: 6,
          background: integrity.valid ? C.greenFaint : C.amberFaint,
          border: `1px solid ${integrity.valid ? C.green : C.amber}25`,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          {integrity.valid ? <Check size={11} color={C.green} /> : <AlertTriangle size={11} color={C.amber} />}
          <span style={{ fontFamily: MONO, fontSize: 8, color: integrity.valid ? C.green : C.amber }}>
            {integrity.valid ? 'SIGNATURE VALIDE' : summary.signed ? 'SIGNATURE NON VÉRIFIÉE' : 'NON SIGNÉE'}
          </span>
        </div>

        {trace.scenarios_tested?.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em', marginBottom: 6 }}>
              SCÉNARIOS TESTÉS
            </div>
            {trace.scenarios_tested.map((s, i) => {
              const outcomeColor = s.outcome === 'success' ? C.green
                : s.outcome === 'failure' ? C.red
                : C.amber;
              return (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 4,
                  padding: 4,
                  background: C.bgInset,
                  borderRadius: 3,
                }}>
                  <div style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: outcomeColor,
                    flexShrink: 0,
                  }} />
                  <span style={{ fontFamily: MONO, fontSize: 9, color: C.textSecond, flex: 1 }}>
                    {s.scenario_id?.replace('scenario_', '')}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: 8, color: outcomeColor }}>
                    {s.outcome?.toUpperCase()}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => onSave?.(trace)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              padding: '6px 0',
              background: C.cyanFaint,
              border: `1px solid ${C.cyan}40`,
              borderRadius: 3,
              cursor: 'pointer',
              color: C.cyan,
              fontFamily: MONO,
              fontSize: 8,
              letterSpacing: '0.1em',
            }}
          >
            <Save size={10} />
            ENREGISTRER
          </button>
          <button
            onClick={() => onDownload?.(trace)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              padding: '6px 0',
              background: C.violetFaint,
              border: `1px solid ${C.violet}40`,
              borderRadius: 3,
              cursor: 'pointer',
              color: C.violet,
              fontFamily: MONO,
              fontSize: 8,
              letterSpacing: '0.1em',
            }}
          >
            <Download size={10} />
            EXPORTER
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SLIDER D'AXE
// ═══════════════════════════════════════════════════════════════════════════════

const AxisSlider = ({ axis, value, onChange }) => {
  return (
    <div style={{
      padding: '8px 10px',
      background: C.bgInset,
      border: `1px solid ${C.border}`,
      borderRadius: 4,
      marginBottom: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily: MONO, fontSize: 8, color: axis.poleA.color, letterSpacing: '0.08em' }}>
          {axis.poleA.shortLabel}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.08em' }}>
          {axis.shortLabel?.toUpperCase() || axis.id.toUpperCase()}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 8, color: axis.poleB.color, letterSpacing: '0.08em' }}>
          {axis.poleB.shortLabel}
        </span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={value ?? 0.5}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: axis.poleB.color, cursor: 'pointer' }}
      />
      <div style={{
        marginTop: 4,
        fontFamily: MONO,
        fontSize: 8,
        color: C.textMuted,
        textAlign: 'center',
      }}>
        {Math.round((value ?? 0.5) * 100)}%
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CALIBRATION — élicitation indirecte de la position
//
// Le problème que ce module traite : demander à quelqu'un de se positionner
// directement sur "contrôle 15% / navigation 85%" souffre d'un biais connu —
// les personnes les plus marquées par un pôle sont souvent celles qui s'y
// perçoivent le moins (un Contrôleur ne se déclare pas Contrôleur). La
// calibration ne remplace pas l'auto-positionnement (l'éditeur à sliders
// reste disponible) : elle produit une SECONDE position, déduite d'un choix
// forcé entre deux comportements concrets par axe — puis expose l'écart entre
// les deux. C'est cet écart qui porte la valeur pédagogique, pas la position
// déduite elle-même.
//
// Aucune donnée n'est inventée : les deux affirmations par axe et la question
// de cadrage viennent telles quelles de posturesData2.js (poleA.description,
// poleB.description, epistemologicalQuestion).
// ═══════════════════════════════════════════════════════════════════════════════

const LIKERT_LEVELS = [
  { value: 0.1, weight: -2 },
  { value: 0.3, weight: -1 },
  { value: 0.5, weight: 0 },
  { value: 0.7, weight: 1 },
  { value: 0.9, weight: 2 },
];

const CalibrationModal = ({ axes, answers, onAnswer, onClose }) => {
  const [step, setStep] = useState(0);
  const axis = axes[step];
  if (!axis) return null;

  const answered = answers[axis.id];
  const isLast = step === axes.length - 1;
  const allAnswered = axes.every(a => answers[a.id] !== undefined);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(4,4,10,0.72)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.97 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: C.bgCard,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
        }}
      >
        <div style={{ height: 2, background: `linear-gradient(90deg, ${axis.poleA.color}, ${axis.poleB.color})` }} />

        <div style={{ padding: '16px 20px 4px' }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.14em' }}>
              CALIBRATION · {step + 1}/{axes.length}
            </span>
            <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 3, marginBottom: 16 }}>
            {axes.map((a, i) => (
              <div key={a.id} style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: answers[a.id] !== undefined ? C.violet : (i === step ? C.violetFaint : C.border),
              }} />
            ))}
          </div>

          <div style={{ fontFamily: DISPLAY, fontSize: 15, color: C.textPrimary, fontWeight: 600, lineHeight: 1.4, marginBottom: 4 }}>
            {axis.epistemologicalQuestion}
          </div>
          <div style={{ fontFamily: BODY, fontSize: 10, color: C.textMuted, marginBottom: 16 }}>
            Sous pression, dans la plupart des situations, je suis plus proche de…
          </div>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {LIKERT_LEVELS.map(level => {
              const isSelected = answered === level.value;
              const isNeutral = level.weight === 0;
              const pole = isNeutral ? null : (level.weight < 0 ? axis.poleA : axis.poleB);
              const labelPrefix = Math.abs(level.weight) === 2 ? 'Tout à fait' : 'Plutôt';

              return (
                <button
                  key={level.value}
                  onClick={() => onAnswer(axis.id, level.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    background: isSelected ? (pole ? pole.color + '15' : C.violetFaint) : C.bgInset,
                    border: `1px solid ${isSelected ? (pole ? pole.color + '60' : C.violet + '50') : C.border}`,
                    borderRadius: 5,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.12s',
                  }}
                >
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: pole ? pole.color : C.textMuted,
                    boxShadow: isSelected ? `0 0 8px ${pole ? pole.color : C.textMuted}` : 'none',
                  }} />
                  <div style={{ flex: 1 }}>
                    {isNeutral ? (
                      <span style={{ fontFamily: BODY, fontSize: 11, color: C.textSecond }}>
                        Ça dépend vraiment des situations
                      </span>
                    ) : (
                      <>
                        <div style={{ fontFamily: MONO, fontSize: 8, color: pole.color, letterSpacing: '0.08em', marginBottom: 2 }}>
                          {labelPrefix.toUpperCase()} — {pole.label.toUpperCase()}
                        </div>
                        <div style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary }}>
                          {pole.description}
                        </div>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 20px',
          borderTop: `1px solid ${C.border}`,
        }}>
          <button
            disabled={step === 0}
            onClick={() => setStep(s => Math.max(0, s - 1))}
            style={{
              padding: '7px 14px',
              background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              color: step === 0 ? C.textMuted + '50' : C.textMuted,
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '0.08em',
              cursor: step === 0 ? 'default' : 'pointer',
            }}
          >
            PRÉCÉDENT
          </button>
          <span style={{ flex: 1 }} />
          {isLast ? (
            <button
              onClick={onClose}
              disabled={!allAnswered}
              style={{
                padding: '7px 16px',
                background: allAnswered ? C.violetFaint : C.bgInset,
                border: `1px solid ${allAnswered ? C.violet + '60' : C.border}`,
                borderRadius: 4,
                color: allAnswered ? C.violet : C.textMuted,
                fontFamily: MONO,
                fontSize: 9,
                letterSpacing: '0.08em',
                cursor: allAnswered ? 'pointer' : 'default',
              }}
            >
              VOIR LE RÉSULTAT
            </button>
          ) : (
            <button
              onClick={() => setStep(s => Math.min(axes.length - 1, s + 1))}
              disabled={answered === undefined}
              style={{
                padding: '7px 16px',
                background: answered !== undefined ? C.violetFaint : C.bgInset,
                border: `1px solid ${answered !== undefined ? C.violet + '60' : C.border}`,
                borderRadius: 4,
                color: answered !== undefined ? C.violet : C.textMuted,
                fontFamily: MONO,
                fontSize: 9,
                letterSpacing: '0.08em',
                cursor: answered !== undefined ? 'pointer' : 'default',
              }}
            >
              SUIVANT
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Panneau d'écart — auto-perception (sliders) vs position déduite (calibration) ──

const CalibrationGapPanel = ({ axes, selfPosition, calibratedPosition, onAdopt, onRecalibrate }) => {
  if (!calibratedPosition) return null;

  const rows = axes.filter(a => calibratedPosition[a.id] !== null && calibratedPosition[a.id] !== undefined);
  if (rows.length === 0) return null;

  const avgGap = rows.reduce((sum, a) => sum + Math.abs((selfPosition[a.id] ?? 0.5) - calibratedPosition[a.id]), 0) / rows.length;
  const gapPct = Math.round(avgGap * 100);
  const gapColor = gapPct < 10 ? C.green : gapPct < 25 ? C.amber : C.red;
  const gapLabel = gapPct < 10 ? 'Auto-perception alignée' : gapPct < 25 ? 'Écart modéré' : 'Écart marqué';

  return (
    <div style={{
      marginTop: 10,
      marginBottom: 4,
      padding: 10,
      background: gapColor + '08',
      border: `1px solid ${gapColor}30`,
      borderRadius: 5,
    }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, color: gapColor, letterSpacing: '0.1em' }}>
          {gapLabel.toUpperCase()}
        </span>
        <span style={{ fontFamily: DISPLAY, fontSize: 13, color: gapColor, fontWeight: 700 }}>
          {gapPct}%
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 10 }}>
        {rows.map(axis => {
          const perceived = selfPosition[axis.id] ?? 0.5;
          const calibrated = calibratedPosition[axis.id];
          const delta = Math.abs(perceived - calibrated);
          return (
            <div key={axis.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.08em' }}>
                  {axis.shortLabel?.toUpperCase() || axis.id.toUpperCase()}
                </span>
                {delta > 0.15 && (
                  <span style={{ fontFamily: MONO, fontSize: 8, color: C.amber }}>
                    Δ {Math.round(delta * 100)}%
                  </span>
                )}
              </div>
              <div style={{ position: 'relative', height: 12, background: C.bgInset, borderRadius: 3 }}>
                <div style={{
                  position: 'absolute', top: 1, bottom: 1,
                  left: `calc(${perceived * 100}% - 1px)`,
                  width: 2, background: C.cyan, borderRadius: 1,
                }} title="Auto-perception" />
                <div style={{
                  position: 'absolute', top: 1, bottom: 1,
                  left: `calc(${calibrated * 100}% - 1px)`,
                  width: 2, background: C.violet, borderRadius: 1,
                }} title="Déduit de la calibration" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3" style={{ marginBottom: 9 }}>
        <span className="flex items-center gap-1" style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted }}>
          <span style={{ width: 7, height: 2, background: C.cyan, display: 'inline-block' }} /> Auto-perception
        </span>
        <span className="flex items-center gap-1" style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted }}>
          <span style={{ width: 7, height: 2, background: C.violet, display: 'inline-block' }} /> Déduit
        </span>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onAdopt}
          style={{
            flex: 1,
            padding: '6px 0',
            background: C.violetFaint,
            border: `1px solid ${C.violet}50`,
            borderRadius: 3,
            color: C.violet,
            fontFamily: MONO,
            fontSize: 8,
            letterSpacing: '0.08em',
            cursor: 'pointer',
          }}
        >
          ADOPTER LA POSITION DÉDUITE
        </button>
        <button
          onClick={onRecalibrate}
          style={{
            padding: '6px 10px',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            color: C.textMuted,
            fontFamily: MONO,
            fontSize: 8,
            letterSpacing: '0.08em',
            cursor: 'pointer',
          }}
        >
          REFAIRE
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export const VisualizationPostures = ({ config: externalConfig }) => {
  const config = externalConfig || posturesVisualization3DData;

  // ── État de base ──
  const [actorPositions, setActorPositions] = useState(() => {
    const initial = {};
    config.actors?.forEach(a => { initial[a.id] = { ...a.position }; });
    return initial;
  });
  const [hoveredTarget, setHoveredTarget] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showAxisSliders, setShowAxisSliders] = useState(true);
  const [editMode, setEditMode] = useState('view');
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showAxes, setShowAxes] = useState(true);

  // ── État mode scénario ──
  const [mode, setMode] = useState('atelier');   // 'atelier' | 'scenario' | 'trace'
  const [activeScenario, setActiveScenario] = useState(null);
  const [activeDomain, setActiveDomain] = useState(null);
  // Scénario survolé dans le radar passif — distinct de activeScenario :
  // ne change ni le mode ni la sélection, juste un aperçu temporaire dans la scène.
  const [radarHoverScenario, setRadarHoverScenario] = useState(null);

  // ── État mode trace ──
  const [currentTrace, setCurrentTrace] = useState(null);
  const [traceMetadata, setTraceMetadata] = useState({
    actor_id: 'anonymous',
    actor_label: 'Utilisateur',
    referential: '',
    profile: 'apprenant',
    domain: '',
  });

  // ── État profil ──
  const [activeProfile, setActiveProfile] = useState('apprenant');
  const [livretOpen, setLivretOpen] = useState(false);
  const [livretModule, setLivretModule] = useState('00');

  // ── État calibration (élicitation indirecte) ──
  const [calibrationAnswers, setCalibrationAnswers] = useState({}); // { axisId: 0.1..0.9 }
  const [calibrationOpen, setCalibrationOpen] = useState(false);


  const openLivret = useCallback((context) => {
  const moduleId = livretContextMap[context] || '00';
  setLivretModule(moduleId);
  setLivretOpen(true);
}, []);

  // ── Position self ──
  const selfPosition = useMemo(
    () => actorPositions.self || config.actors?.[0]?.position || {},
    [actorPositions, config.actors]
  );

  // ── Position déduite de la calibration — null tant qu'aucune question n'a été répondue ──
  const calibratedPosition = useMemo(() => {
    if (Object.keys(calibrationAnswers).length === 0) return null;
    const pos = {};
    config.axes?.forEach(axis => {
      pos[axis.id] = calibrationAnswers[axis.id] ?? null;
    });
    return pos;
  }, [calibrationAnswers, config.axes]);

  // ── Écart moyen entre auto-perception et position déduite, sur les axes répondus ──
  const calibrationGap = useMemo(() => {
    if (!calibratedPosition) return null;
    const rows = config.axes?.filter(a => calibratedPosition[a.id] !== null) || [];
    if (rows.length === 0) return null;
    const sum = rows.reduce((acc, a) => acc + Math.abs((selfPosition[a.id] ?? 0.5) - calibratedPosition[a.id]), 0);
    return sum / rows.length;
  }, [calibratedPosition, selfPosition, config.axes]);

  const handleCalibrationAnswer = useCallback((axisId, value) => {
    setCalibrationAnswers(prev => ({ ...prev, [axisId]: value }));
  }, []);

  const handleAdoptCalibration = useCallback(() => {
    if (!calibratedPosition) return;
    setActorPositions(prev => ({
      ...prev,
      self: {
        ...prev.self,
        ...Object.fromEntries(Object.entries(calibratedPosition).filter(([, v]) => v !== null)),
      },
    }));
  }, [calibratedPosition]);

  const handleResetCalibration = useCallback(() => {
    setCalibrationAnswers({});
    setCalibrationOpen(true);
  }, []);

  // ── Archétype le plus proche ──
  const nearestArchetype = useMemo(() => {
    let nearest = null;
    let minDist = Infinity;
    config.archetypes?.forEach(a => {
      const d = distance6D(selfPosition, a.position);
      if (d < minDist) { minDist = d; nearest = a; }
    });
    return nearest ? { archetype: nearest, distance: minDist } : null;
  }, [selfPosition, config.archetypes]);

  // ── Classement des postures les plus proches — au-delà du seul plus proche,
  // avec un pourcentage de proximité (distance6D est bornée par 1 : RMS de 6
  // écarts normalisés sur [0,1]). ──
  const rankedArchetypes = useMemo(() => {
    const list = (config.archetypes || []).map(a => ({
      archetype: a,
      distance: distance6D(selfPosition, a.position),
    }));
    list.sort((a, b) => a.distance - b.distance);
    return list.slice(0, 3).map(r => ({
      ...r,
      proximity: Math.max(0, Math.min(100, Math.round((1 - r.distance) * 100))),
    }));
  }, [selfPosition, config.archetypes]);

  // ── Trajectoire recommandée : celle qui part de la posture la plus proche ──
  const recommendedTrajectory = useMemo(() => {
    if (!nearestArchetype || !config.trajectories) return null;
    return config.trajectories.find(t => t.from === nearestArchetype.archetype.id) || null;
  }, [nearestArchetype, config.trajectories]);

  // ── Tensions actives : les deux axes impliqués sont chacun marqués (écart
  // au centre > 0.3), quel que soit le pôle vers lequel ils penchent — c'est
  // précisément quand les deux tendances sont fortes qu'elles se heurtent. ──
  const activeTensions = useMemo(() => {
    if (!config.tensions) return [];
    return config.tensions.filter(t =>
      (t.between || []).length > 0 &&
      t.between.every(axisId => Math.abs((selfPosition[axisId] ?? 0.5) - 0.5) > 0.3)
    );
  }, [config.tensions, selfPosition]);

  // ── Ponts inter-domaines concernant le domaine actif ──
  const activeDomainBridges = useMemo(() => {
    if (!activeDomain || !config.domainMapping?.bridges) return [];
    return config.domainMapping.bridges.filter(b => b.from === activeDomain || b.to === activeDomain);
  }, [activeDomain, config.domainMapping]);

  // ── Scénarios filtrés par domaine ──
  const availableScenarios = useMemo(() => {
    if (!activeDomain) return config.scenarios || [];
    return getScenariosForDomain(config, activeDomain);
  }, [config, activeDomain]);

  // ── Libellé de l'élément survolé — pour une lisibilité immédiate dans la scène ──
  const hoveredLabel = useMemo(() => {
    if (!hoveredTarget) return null;
    const arch = config.archetypes?.find(a => a.id === hoveredTarget);
    if (arch) return { label: arch.label, color: arch.color, kind: 'Posture' };
    const actor = config.actors?.find(a => a.id === hoveredTarget);
    if (actor) return { label: actor.label, color: actor.color || C.cyan, kind: actor.type === 'self' ? 'Vous' : 'Acteur' };
    return null;
  }, [hoveredTarget, config.archetypes, config.actors]);

  // ── Handlers ──
  const handleItemClick = useCallback((userData) => {
    if (userData.type === 'self') {
      setShowEditor(true);
      setSelectedItem(null);
      return;
    }
    if (userData.type === 'archetype') {
      const archetype = config.archetypes.find(a => a.id === userData.id);
      if (archetype) setSelectedItem({ type: 'archetype', data: archetype });
      return;
    }
    if (userData.type === 'trajectory') {
      const traj = config.trajectories.find(t => t.id === userData.id);
      if (traj) setSelectedItem({ type: 'trajectory', data: traj });
      return;
    }
    if (userData.type === 'actor') {
      setSelectedItem({ type: 'actor', data: userData });
    }
  }, [config]);

  const updateSelfPosition = useCallback((axisId, value) => {
    setActorPositions(prev => ({
      ...prev,
      self: { ...prev.self, [axisId]: value },
    }));
  }, []);

  const handleActorDragStart = useCallback((actorId) => {
    setSelectedItem(null);
  }, []);

  const handleActorDragEnd = useCallback((actorId, worldPosition) => {
    setActorPositions(prev => {
      const current = prev[actorId] || {};
      const newPosition = snapPosition6D(position3DTo6D(worldPosition, current));
      if (currentTrace && actorId === 'self') {
        setCurrentTrace(t => appendPosition(t, newPosition));
      }
      return { ...prev, [actorId]: newPosition };
    });
  }, [currentTrace]);

  // ═══════════════════════════════════════════════════════════════════════════
  // MODE TRACE — gestion de session
  // ═══════════════════════════════════════════════════════════════════════════

  const handleStartTrace = useCallback(() => {
    const trace = createTrace({
      ...traceMetadata,
      position_initial: { ...selfPosition },
    });
    const withInitial = appendPosition(trace, selfPosition);
    setCurrentTrace(withInitial);
    setSelectedItem({ type: 'trace', data: withInitial });
  }, [traceMetadata, selfPosition]);

  const handleFinalizeTrace = useCallback(() => {
    if (!currentTrace) return;
    const finalized = finalizeTrace(currentTrace, selfPosition);
    const signed = signTrace(finalized);
    setCurrentTrace(signed);
    setSelectedItem({ type: 'trace', data: signed });
  }, [currentTrace, selfPosition]);

  const handleSaveTrace = useCallback((trace) => {
    const success = saveSession(trace);
    if (success) {
      setTraceMetadata(prev => ({ ...prev }));
    }
  }, []);

  const handleDownloadTrace = useCallback((trace) => {
    downloadTrace(trace);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // MODE SCÉNARIO — test et enregistrement
  // ═══════════════════════════════════════════════════════════════════════════

  const handleRecordScenarioResult = useCallback((scenarioId, outcome) => {
    if (!currentTrace) return;
    const scenario = config.scenarios?.find(s => s.id === scenarioId);
    if (!scenario) return;

    let closest = null;
    let minDist = Infinity;
    config.archetypes?.forEach(a => {
      if (!scenario.behaviors?.[a.id]) return;
      const d = distance6D(selfPosition, a.position);
      if (d < minDist) { minDist = d; closest = a; }
    });

    const behavior = closest ? scenario.behaviors[closest.id] : null;

    const updated = appendScenarioResult(currentTrace, {
      scenario_id: scenarioId,
      posture_before: { ...selfPosition },
      posture_after: { ...selfPosition },
      chosen_behavior: behavior?.behavior || 'unknown',
      outcome: outcome || behavior?.risk === 'low' ? 'success' : behavior?.risk === 'critical' ? 'failure' : 'moderate',
      observations: [],
    });

    setCurrentTrace(updated);
    setSelectedItem({ type: 'trace', data: updated });
  }, [currentTrace, selfPosition, config]);

  // ═══════════════════════════════════════════════════════════════════════════
  // GESTION DES MODES
  // ═══════════════════════════════════════════════════════════════════════════

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
    if (newMode === 'atelier') {
      setActiveScenario(null);
      setActiveDomain(null);
    } else if (newMode === 'scenario') {
      setActiveScenario(config.scenarios?.[0]?.id || null);
    } else if (newMode === 'trace') {
      setActiveScenario(null);
      setActiveDomain(null);
    }
    setSelectedItem(null);
  }, [config.scenarios]);

  // ── Profil actif ──
  const activeProfileData = config.profiles?.find(p => p.id === activeProfile);

  const canSee = (section) => {
    if (!activeProfileData) return true;
    return activeProfileData.visibleSections?.includes(section);
  };

  useEffect(() => {
  const timeouts = [];
  timeouts.push(setTimeout(() => {
    timeouts.push(setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50));
  }, 16));
  return () => timeouts.forEach(t => clearTimeout(t));
}, []);

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden" style={{ background: C.bg }}>
      {/* ═══════════════════════════════════════════════════════════════════════
          HEADER
          ═══════════════════════════════════════════════════════════════════════ */}
      <header style={{
        height: 62,
        flexShrink: 0,
        background: C.bgPanel,
        borderBottom: `1px solid ${C.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',        
      }}>
        <div className="flex items-center gap-3">
          <Compass size={16} color={C.violet} />
          <div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.textAccent, letterSpacing: '0.2em' }}>
              {config.targetLabel?.toUpperCase() || 'ATLAS DES POSTURES'}
            </div>
            <div style={{ fontFamily: BODY, fontSize: 10, color: C.textMuted, marginTop: 1 }}>
              {config.targetSubtitle}
            </div>
          </div>

          {/* Mode switcher */}
          <div style={{
            display: 'flex',
            gap: 2,
            marginLeft: 24,
            padding: 3,
            background: C.bgInset,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
          }}>
            {[
              { id: 'atelier', label: 'ATELIER', icon: Compass },
              { id: 'scenario', label: 'SCÉNARIO', icon: Target },
              { id: 'trace', label: 'TRACE', icon: FileText },
            ].map(m => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => handleModeChange(m.id)}
                  title={
                    m.id === 'atelier' ? 'Explorer les axes et les postures (Module 01)'
                    : m.id === 'scenario' ? 'Comprendre ce qu\'une posture produit (Module 04)'
                    : 'Enregistrer une trajectoire décisionnelle (Module 08)'
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    background: active ? C.violetFaint : 'transparent',
                    border: 'none',
                    borderRadius: 3,
                    cursor: 'pointer',
                    color: active ? C.violet : C.textMuted,
                    fontFamily: MONO,
                    fontSize: 8,
                    letterSpacing: '0.1em',
                  }}

                >
                  <Icon size={10} />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Domaine actif */}
          {mode !== 'trace' && (
            <select
              value={activeDomain || ''}
              onChange={(e) => setActiveDomain(e.target.value || null)}
              style={{
                padding: '5px 8px',
                background: C.bgInset,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                color: C.textSecond,
                fontFamily: MONO,
                fontSize: 9,
                letterSpacing: '0.08em',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="">TOUS DOMAINES</option>
              {config.domains?.map(d => (
                <option key={d.id} value={d.id}>
                  {d.label.toUpperCase()}
                </option>
              ))}
            </select>
          )}

          {/* Scénario actif */}
          

          {/* Profil */}
          <select
            value={activeProfile}
            onChange={(e) => setActiveProfile(e.target.value)}
            style={{
              padding: '5px 8px',
              background: C.bgInset,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              color: C.textSecond,
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '0.08em',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {config.profiles?.map(p => (
              <option key={p.id} value={p.id}>
                {p.label.toUpperCase()}
              </option>
            ))}
          </select>

          <div style={{ width: 1, height: 16, background: C.border }} />

          {/* Toggle axes */}
          <button
            onClick={() => setShowAxes(v => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 10px',
              background: showAxes ? C.violetFaint : 'transparent',
              border: `1px solid ${showAxes ? C.violet + '40' : C.border}`,
              borderRadius: 3,
              cursor: 'pointer',
              color: showAxes ? C.violet : C.textMuted,
              fontFamily: MONO,
              fontSize: 8,
              letterSpacing: '0.1em',
            }}
          >
            <Eye size={10} />
            AXES
          </button>

          {/* Toggle drag */}
          <button
            onClick={() => setEditMode(m => m === 'view' ? 'drag' : 'view')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 10px',
              background: editMode === 'drag' ? C.cyanFaint : 'transparent',
              border: `1px solid ${editMode === 'drag' ? C.cyan + '40' : C.border}`,
              borderRadius: 3,
              cursor: 'pointer',
              color: editMode === 'drag' ? C.cyan : C.textMuted,
              fontFamily: MONO,
              fontSize: 8,
              letterSpacing: '0.1em',
            }}
          >
            <Crosshair size={10} />
            {editMode === 'drag' ? 'DÉPLACEMENT' : 'DÉPLACER'}
          </button>

          <motion.button
            onClick={() => setCalibrationOpen(true)}
            animate={
              mode === 'scenario' && !calibratedPosition && !calibrationOpen
                ? {
                    boxShadow: [
                      '0 0 0px rgba(167,139,250,0)',
                      '0 0 12px rgba(167,139,250,0.65)',
                      '0 0 0px rgba(167,139,250,0)',
                    ],
                  }
                : { boxShadow: '0 0 0px rgba(167,139,250,0)' }
            }
            transition={
              mode === 'scenario' && !calibratedPosition && !calibrationOpen
                ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.2 }
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              background: mode === 'scenario' && !calibratedPosition ? C.violetFaint : C.bgInset,
              border: `1px solid ${mode === 'scenario' && !calibratedPosition ? C.violet + '60' : C.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              color: mode === 'scenario' && !calibratedPosition ? C.violet : C.textSecond,
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '0.1em',
            }}
            title="Déduire votre position à partir de choix comportementaux, plutôt que de la déclarer directement"
          >
            <Sparkles size={11} />
            CALIBRER
          </motion.button>

          <button
            onClick={() => setShowEditor(v => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              background: showEditor ? C.violetFaint : C.bgInset,
              border: `1px solid ${showEditor ? C.violet + '60' : C.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              color: showEditor ? C.violet : C.textSecond,
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '0.1em',
            }}
          >
            <Settings size={11} />
            ME POSITIONNER
          </button>
          <HelpButton
            onClick={() => openLivret('positionEditor')}
            color={C.violet}
            title="Comment se positionner (Module 06)"
          />

          <button
            onClick={() => { setLivretModule('00'); setLivretOpen(true); }}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 10px',
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                cursor: 'pointer',
                color: C.textMuted,
                fontFamily: MONO,
                fontSize: 8,
                letterSpacing: '0.1em',
            }}
            title="Ouvrir le livret"
            >
            <BookOpen size={10} />
            LIVRET
          </button>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════════════
          BODY
          ═══════════════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* ═══ LEFT PANEL ═══ */}
        <motion.aside
        animate={{ width: showLeftPanel ? 260 : 36 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        onAnimationComplete={() => {
          window.dispatchEvent(new Event('resize'));
        }}
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          background: C.bgPanel,
          borderRight: `1px solid ${C.border}`,
          overflow: 'hidden',
        }}
      >
          <button
            onClick={() => setShowLeftPanel(v => !v)}
            style={{
              height: 36,
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px',
              gap: 6,
              cursor: 'pointer',
              borderBottom: `1px solid ${C.border}`,
              background: 'transparent',
              color: C.textMuted,
              flexShrink: 0,
            }}
          >
            <Layers size={12} color={C.textAccent} />
            {showLeftPanel && (
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.textSecond, letterSpacing: '0.1em' }}>
                {mode === 'scenario' ? 'SCÉNARIOS' : mode === 'trace' ? 'SESSIONS' : 'AXES'}
              </span>
            )}
          </button>

          {showLeftPanel && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 8, scrollbarWidth: 'none' }}>
              {/* ── MODE ATELIER : axes ── */}
              {mode === 'atelier' && (
                <>
                  <div style={{
                    fontFamily: MONO,
                    fontSize: 8,
                    color: C.textMuted,
                    letterSpacing: '0.1em',
                    marginBottom: 8,
                    padding: '0 4px',
                  }}>
                    {config.axes?.length || 0} DIMENSIONS
                  </div>

                  {config.axes?.map(axis => {
                    const isSelected = selectedItem?.type === 'axis' && selectedItem.data.id === axis.id;
                    return (
                      <button
                        key={axis.id}
                        onClick={() => setSelectedItem({ type: 'axis', data: axis })}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          background: isSelected ? C.violetFaint : C.bgInset,
                          border: `1px solid ${isSelected ? C.violet + '60' : C.border}`,
                          borderRadius: 4,
                          marginBottom: 6,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{
                          fontFamily: MONO,
                          fontSize: 8,
                          color: isSelected ? C.violet : C.textSecond,
                          letterSpacing: '0.08em',
                          marginBottom: 4,
                        }}>
                          {axis.label.toUpperCase()}
                        </div>
                        <div style={{
                          fontFamily: BODY,
                          fontSize: 10,
                          color: C.textMuted,
                          lineHeight: 1.5,
                          marginBottom: 6,
                        }}>
                          {axis.description}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontFamily: MONO, fontSize: 8, color: axis.poleA.color }}>
                            {axis.poleA.shortLabel}
                          </span>
                          <span style={{ fontFamily: MONO, fontSize: 8, color: axis.poleB.color }}>
                            {axis.poleB.shortLabel}
                          </span>
                        </div>
                      </button>
                    );
                  })}

                  {canSee('tensions') && config.tensions?.length > 0 && (
                    <>
                      <div style={{
                        fontFamily: MONO,
                        fontSize: 8,
                        color: C.textMuted,
                        letterSpacing: '0.1em',
                        marginTop: 14,
                        marginBottom: 8,
                        padding: '0 4px',
                      }}>
                        TENSIONS SYSTÉMIQUES
                      </div>
                      {config.tensions.map(tension => {
                        const isSelected = selectedItem?.type === 'tension' && selectedItem.data.id === tension.id;
                        const isActive = activeTensions.some(t => t.id === tension.id);
                        return (
                          <button
                            key={tension.id}
                            onClick={() => setSelectedItem({ type: 'tension', data: tension })}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 10px',
                              background: isSelected ? C.amberFaint : (isActive ? C.redFaint : 'transparent'),
                              border: `1px solid ${isSelected ? C.amber + '40' : (isActive ? C.red + '50' : C.border)}`,
                              borderRadius: 3,
                              marginBottom: 4,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <AlertTriangle size={10} color={isActive ? C.red : C.amber} />
                            <span style={{ fontFamily: MONO, fontSize: 9, color: C.textSecond, flex: 1 }}>
                              {tension.between?.join(' × ')}
                            </span>
                            {isActive && (
                              <span style={{ fontFamily: MONO, fontSize: 7, color: C.red, letterSpacing: '0.06em' }}>
                                ACTIVE
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </>
                  )}

                  {/* Ponts inter-domaines — donnée présente dans domainMapping
                      mais jamais exposée jusqu'ici ; visible dès qu'un domaine
                      est sélectionné et concerné par au moins un pont. */}
                  {activeDomainBridges.length > 0 && (
                    <>
                      <div style={{
                        fontFamily: MONO,
                        fontSize: 8,
                        color: C.textMuted,
                        letterSpacing: '0.1em',
                        marginTop: 14,
                        marginBottom: 8,
                        padding: '0 4px',
                      }}>
                        PONTS INTER-DOMAINES
                      </div>
                      {activeDomainBridges.map(bridge => {
                        const isSelected = selectedItem?.type === 'bridge' && selectedItem.data.id === bridge.id;
                        const otherId = bridge.from === activeDomain ? bridge.to : bridge.from;
                        const otherDomain = config.domains?.find(d => d.id === otherId);
                        return (
                          <button
                            key={bridge.id}
                            onClick={() => setSelectedItem({ type: 'bridge', data: bridge })}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 10px',
                              background: isSelected ? C.cyanFaint : 'transparent',
                              border: `1px solid ${isSelected ? C.cyan + '40' : C.border}`,
                              borderRadius: 3,
                              marginBottom: 4,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <GitBranch size={10} color={C.cyan} />
                            <span style={{ fontFamily: MONO, fontSize: 9, color: C.textSecond }}>
                              → {otherDomain?.label || otherId}
                            </span>
                          </button>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {/* ── MODE SCÉNARIO : scénarios ── */}
              {mode === 'scenario' && (
                <>
                  <div style={{
                    fontFamily: MONO,
                    fontSize: 8,
                    color: C.textMuted,
                    letterSpacing: '0.1em',
                    marginBottom: 8,
                    padding: '0 4px',
                  }}>
                    {availableScenarios.length} SCÉNARIOS
                  </div>

                  {availableScenarios.map(scenario => {
                    const isActive = activeScenario === scenario.id;
                    const projection = activeDomain ? scenario.projections?.[activeDomain] : null;

                    return (
                      <button
                        key={scenario.id}
                        onClick={() => {
                          setActiveScenario(scenario.id);
                          setSelectedItem({ type: 'scenario', data: scenario });
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          background: isActive ? C.orangeFaint : C.bgInset,
                          border: `1px solid ${isActive ? C.orange + '60' : C.border}`,
                          borderRadius: 4,
                          marginBottom: 6,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                          <Target size={10} color={isActive ? C.orange : C.textMuted} />
                          <span style={{
                            fontFamily: MONO,
                            fontSize: 8,
                            color: isActive ? C.orange : C.textSecond,
                            letterSpacing: '0.08em',
                          }}>
                            {scenario.shortLabel || scenario.label.toUpperCase()}
                          </span>
                        </div>
                        {projection && (
                          <div style={{
                            fontFamily: BODY,
                            fontSize: 10,
                            color: C.textPrimary,
                            marginBottom: 3,
                            fontWeight: 500,
                          }}>
                            {projection.label}
                          </div>
                        )}
                        <div style={{
                          fontFamily: BODY,
                          fontSize: 10,
                          color: C.textMuted,
                          lineHeight: 1.4,
                        }}>
                          {projection?.example || scenario.description.slice(0, 80) + '…'}
                        </div>
                      </button>
                    );
                  })}

                  {availableScenarios.length === 0 && (
                    <div style={{
                      padding: 20,
                      textAlign: 'center',
                      fontFamily: MONO,
                      fontSize: 9,
                      color: C.textMuted,
                    }}>
                      AUCUN SCÉNARIO<br />POUR CE DOMAINE
                    </div>
                  )}
                </>
              )}

              {/* ── MODE TRACE : sessions ── */}
              {mode === 'trace' && (
                <>
                  {currentTrace ? (
                    <>
                      <div style={{
                        fontFamily: MONO,
                        fontSize: 8,
                        color: C.textMuted,
                        letterSpacing: '0.1em',
                        marginBottom: 8,
                        padding: '0 4px',
                      }}>
                        SESSION EN COURS
                      </div>

                      <button
                        onClick={() => setSelectedItem({ type: 'trace', data: currentTrace })}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          background: C.cyanFaint,
                          border: `1px solid ${C.cyan + '60'}`,
                          borderRadius: 4,
                          marginBottom: 8,
                          cursor: 'pointer',
                        }}
                      >
                        <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                          <CircleDot size={10} color={C.cyan} />
                          <span style={{
                            fontFamily: MONO,
                            fontSize: 8,
                            color: C.cyan,
                            letterSpacing: '0.08em',
                          }}>
                            ENREGISTREMENT
                          </span>
                        </div>
                        <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted }}>
                          {currentTrace.session_id}
                        </div>
                        <div style={{ fontFamily: BODY, fontSize: 10, color: C.textSecond, marginTop: 4 }}>
                          {currentTrace.positions_history?.length || 0} positions enregistrées
                        </div>
                      </button>

                      <div className="flex flex-col gap-1 mb-3">
                        {!currentTrace.timestamp_end && (
                          <button
                            onClick={handleFinalizeTrace}
                            style={{
                              padding: '6px 10px',
                              background: C.greenFaint,
                              border: `1px solid ${C.green}40`,
                              borderRadius: 3,
                              cursor: 'pointer',
                              color: C.green,
                              fontFamily: MONO,
                              fontSize: 8,
                              letterSpacing: '0.1em',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                            }}
                          >
                            <Square size={10} />
                            TERMINER LA SESSION
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 4px',
                        marginBottom: 8,
                        }}>
                        <span style={{
                            fontFamily: MONO,
                            fontSize: 8,
                            color: C.textMuted,
                            letterSpacing: '0.1em',
                        }}>
                            NOUVELLE SESSION
                        </span>
                        <HelpButton
                            onClick={() => openLivret('traceStart')}
                            title="Comment enregistrer une trace (Module 08)"
                        />
                      </div>

                      <div style={{
                        padding: 10,
                        background: C.bgInset,
                        border: `1px solid ${C.border}`,
                        borderRadius: 4,
                        marginBottom: 8,
                      }}>
                        {[
                          { key: 'actor_label', label: 'ACTEUR', type: 'text' },
                          { key: 'referential', label: 'RÉFÉRENTIEL', type: 'text' },
                        ].map(field => (
                          <div key={field.key} style={{ marginBottom: 8 }}>
                            <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
                              {field.label}
                            </div>
                            <input
                              type={field.type}
                              value={traceMetadata[field.key]}
                              onChange={(e) => setTraceMetadata(prev => ({ ...prev, [field.key]: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '5px 8px',
                                background: C.bg,
                                border: `1px solid ${C.border}`,
                                borderRadius: 3,
                                color: C.textPrimary,
                                fontFamily: MONO,
                                fontSize: 10,
                                outline: 'none',
                              }}
                            />
                          </div>
                        ))}

                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
                            DOMAINE
                          </div>
                          <select
                            value={traceMetadata.domain}
                            onChange={(e) => setTraceMetadata(prev => ({ ...prev, domain: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '5px 8px',
                              background: C.bg,
                              border: `1px solid ${C.border}`,
                              borderRadius: 3,
                              color: C.textPrimary,
                              fontFamily: MONO,
                              fontSize: 10,
                              outline: 'none',
                            }}
                          >
                            <option value="">—</option>
                            {config.domains?.map(d => (
                              <option key={d.id} value={d.id}>{d.label}</option>
                            ))}
                          </select>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
                            PROFIL
                          </div>
                          <select
                            value={traceMetadata.profile}
                            onChange={(e) => setTraceMetadata(prev => ({ ...prev, profile: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '5px 8px',
                              background: C.bg,
                              border: `1px solid ${C.border}`,
                              borderRadius: 3,
                              color: C.textPrimary,
                              fontFamily: MONO,
                              fontSize: 10,
                              outline: 'none',
                            }}
                          >
                            {config.profiles?.map(p => (
                              <option key={p.id} value={p.id}>{p.label}</option>
                            ))}
                          </select>
                        </div>

                        <button
                          onClick={handleStartTrace}
                          style={{
                            width: '100%',
                            padding: '8px 0',
                            marginTop: 4,
                            background: C.cyanFaint,
                            border: `1px solid ${C.cyan}60`,
                            borderRadius: 3,
                            cursor: 'pointer',
                            color: C.cyan,
                            fontFamily: MONO,
                            fontSize: 9,
                            letterSpacing: '0.1em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 5,
                          }}
                        >
                          <Play size={10} />
                          DÉMARRER
                        </button>
                      </div>
                    </>
                  )}

                  <div style={{
                    fontFamily: MONO,
                    fontSize: 8,
                    color: C.textMuted,
                    letterSpacing: '0.1em',
                    marginTop: 14,
                    marginBottom: 8,
                    padding: '0 4px',
                  }}>
                    SESSIONS SAUVEGARDÉES
                  </div>

                  {(() => {
                    const sessions = listSessions({ limit: 10 });
                    if (sessions.length === 0) {
                      return (
                        <div style={{
                          padding: 12,
                          textAlign: 'center',
                          fontFamily: MONO,
                          fontSize: 9,
                          color: C.textMuted,
                        }}>
                          AUCUNE SESSION
                        </div>
                      );
                    }
                    return sessions.map(s => (
                      <button
                        key={s.session_id}
                        onClick={() => setSelectedItem({ type: 'trace', data: s })}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '6px 10px',
                          background: C.bgInset,
                          border: `1px solid ${C.border}`,
                          borderRadius: 3,
                          marginBottom: 4,
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontFamily: MONO, fontSize: 8, color: C.textSecond, marginBottom: 2 }}>
                          {s.actor_label || s.actor_id}
                        </div>
                        <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted }}>
                          {new Date(s.timestamp_start).toLocaleString('fr-FR')}
                        </div>
                      </button>
                    ));
                  })()}
                </>
              )}
            </div>
          )}

          {showLeftPanel && (
            <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
              <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
                CIBLE
              </div>
              <div style={{ fontFamily: DISPLAY, fontSize: 12, color: C.textAccent, fontWeight: 600 }}>
                {config.targetLabel || '—'}
              </div>
            </div>
          )}
        </motion.aside>

        {/* ═══ MAIN 3D VIEW ═══ */}
        <main style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <PosturesSceneRenderer
            key="scene"
            config={config}
            positions={actorPositions}
            hoveredTarget={hoveredTarget}
            setHoveredTarget={setHoveredTarget}
            onItemClick={handleItemClick}
            showAxes={showAxes}
            editMode={editMode}
            activeScenario={mode === 'scenario' ? activeScenario : null}
            previewScenario={radarHoverScenario}
            activeDomain={activeDomain}
            onActorDragStart={handleActorDragStart}
            onActorDragEnd={handleActorDragEnd}
          />

          {/* Légende contextuelle */}
          <div style={{
            position: 'absolute',
            top: 16,
            left: 16,
            background: C.bgPanel + 'd0',
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            padding: '10px 14px',
            backdropFilter: 'blur(8px)',
            pointerEvents: 'none',
            maxWidth: 300,
          }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.textAccent, letterSpacing: '0.15em', marginBottom: 6 }}>
              {mode === 'scenario' ? 'MODE SCÉNARIO' : mode === 'trace' ? 'MODE TRACE' : 'LECTURE'}
            </div>
            <div style={{ fontFamily: BODY, fontSize: 10, color: C.textSecond, lineHeight: 1.6 }}>
              {mode === 'atelier' && (
                editMode === 'drag' ? (
                  <><strong style={{ color: C.cyan }}>Mode déplacement actif.</strong> Cliquez et glissez un acteur pour ajuster sa position.</>
                ) : (
                  <>Chaque point est une <strong style={{ color: C.violet }}>manière d'être</strong>. Les lignes pointillées sont des trajectoires possibles.</>
                )
              )}
              {mode === 'scenario' && (
                <>Les archétypes se <strong style={{ color: C.green }}>recolorent</strong> selon leur performance dans ce scénario. <strong style={{ color: C.green }}>Vert</strong> = durable, <strong style={{ color: C.red }}>rouge</strong> = critique.</>
              )}
              {mode === 'trace' && (
                currentTrace
                  ? <>Session <strong style={{ color: C.cyan }}>en cours</strong>. Chaque déplacement est enregistré.</>
                  : <>Démarrez une <strong style={{ color: C.cyan }}>nouvelle session</strong> pour enregistrer votre trajectoire décisionnelle.</>
              )}
            </div>

            {/* Écart de calibration et tensions actives — déplacés ici depuis
                le header, qui n'avait plus la place de les accueillir sans
                perdre d'autres éléments (LIVRET notamment) sur les écrans
                étroits. Cet encart est l'endroit naturel pour un état courant
                lu d'un coup d'œil ; pointerEvents réactivé localement pour que
                l'infobulle des tensions reste utilisable. */}
            {(calibrationGap !== null || activeTensions.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, pointerEvents: 'auto' }}>
                {calibrationGap !== null && (
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 9px',
                    background: (calibrationGap > 0.25 ? C.red : calibrationGap > 0.1 ? C.amber : C.green) + '15',
                    border: `1px solid ${(calibrationGap > 0.25 ? C.red : calibrationGap > 0.1 ? C.amber : C.green)}40`,
                    borderRadius: 3,
                    fontFamily: MONO,
                    fontSize: 8,
                    letterSpacing: '0.08em',
                    color: calibrationGap > 0.25 ? C.red : calibrationGap > 0.1 ? C.amber : C.green,
                  }}>
                    ÉCART {Math.round(calibrationGap * 100)}%
                  </span>
                )}

                {activeTensions.length > 0 && (
                  <span
                    title={activeTensions.map(t => t.description).join(' · ')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 9px',
                      background: C.redFaint,
                      border: `1px solid ${C.red}40`,
                      borderRadius: 3,
                      fontFamily: MONO,
                      fontSize: 8,
                      letterSpacing: '0.08em',
                      color: C.red,
                    }}
                  >
                    <AlertTriangle size={9} />
                    {activeTensions.length} TENSION{activeTensions.length > 1 ? 'S' : ''} ACTIVE{activeTensions.length > 1 ? 'S' : ''}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Radar de scénarios — visible en permanence (hors mode trace,
              qui a déjà son propre flux délibéré), aucune action requise. */}
          {mode !== 'trace' && config.scenarios?.length > 0 && (
            <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
              <ScenarioRadar
                scenarios={availableScenarios}
                config={config}
                selfPosition={selfPosition}
                hoveredScenarioId={radarHoverScenario}
                onHoverScenario={setRadarHoverScenario}
                onOpenScenario={(scenario) => {
                  setMode('scenario');
                  setActiveScenario(scenario.id);
                  setSelectedItem({ type: 'scenario', data: scenario });
                }}
              />
            </div>
          )}

          {/* Radar 6 axes — lecture exacte de la position sur les 6
              dimensions. La scène 3D ne rend lisibles que 3 axes sur 6 (les
              3 autres ne sont que des micro-décalages de 0.4×) ; ce panneau
              comble cet angle mort, et superpose la posture la plus proche
              pour visualiser l'écart axe par axe. */}
          <div style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            background: C.bgPanel + 'd8',
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            padding: '10px 12px',
            backdropFilter: 'blur(8px)',
          }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.textAccent, letterSpacing: '0.14em' }}>
                POSITION · 6 AXES
              </span>
              {nearestArchetype && (
                <span style={{ fontFamily: MONO, fontSize: 7, color: nearestArchetype.archetype.color, letterSpacing: '0.06em' }}>
                  vs {nearestArchetype.archetype.shortLabel}
                </span>
              )}
            </div>
            <AxisRadarChart
              axes={config.axes || []}
              position={selfPosition}
              comparePosition={nearestArchetype?.archetype?.position || null}
              compareColor={nearestArchetype?.archetype?.color || C.violet}
              fillColor={C.cyan}
              labelColor={C.textMuted}
              gridColor={C.border}
              size={164}
            />
          </div>

          {/* Pastille de survol — lisibilité immédiate de l'élément pointé,
              sans devoir attendre le clic ni chercher la liste latérale. */}
          <AnimatePresence>
            {hoveredLabel && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.12 }}
                style={{
                  position: 'absolute',
                  top: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: C.bgPanel + 'e8',
                  border: `1px solid ${hoveredLabel.color}60`,
                  borderRadius: 20,
                  padding: '6px 14px',
                  backdropFilter: 'blur(8px)',
                  pointerEvents: 'none',
                  boxShadow: `0 0 16px ${hoveredLabel.color}30`,
                }}
              >
                <div style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: hoveredLabel.color,
                  boxShadow: `0 0 8px ${hoveredLabel.color}`,
                }} />
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em' }}>
                  {hoveredLabel.kind.toUpperCase()}
                </span>
                <span style={{ fontFamily: DISPLAY, fontSize: 12, color: C.textPrimary, fontWeight: 600 }}>
                  {hoveredLabel.label}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Barre d'action scénario */}
          {mode === 'scenario' && activeScenario && currentTrace && (
            <div style={{
              position: 'absolute',
              bottom: 60,
              left: '50%',
              transform: 'translateX(-50%)',
              background: C.bgPanel,
              border: `1px solid ${C.orange}60`,
              borderRadius: 6,
              padding: '10px 16px',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <Target size={14} color={C.orange} />
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.textPrimary, letterSpacing: '0.08em' }}>
                TESTER CE SCÉNARIO
              </span>
              <button
                onClick={() => handleRecordScenarioResult(activeScenario, 'success')}
                style={{
                  padding: '5px 12px',
                  background: C.greenFaint,
                  border: `1px solid ${C.green}40`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  color: C.green,
                  fontFamily: MONO,
                  fontSize: 8,
                  letterSpacing: '0.1em',
                }}
              >
                RÉUSSI
              </button>
              <button
                onClick={() => handleRecordScenarioResult(activeScenario, 'moderate')}
                style={{
                  padding: '5px 12px',
                  background: C.amberFaint,
                  border: `1px solid ${C.amber}40`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  color: C.amber,
                  fontFamily: MONO,
                  fontSize: 8,
                  letterSpacing: '0.1em',
                }}
              >
                PARTIEL
              </button>
              <button
                onClick={() => handleRecordScenarioResult(activeScenario, 'failure')}
                style={{
                  padding: '5px 12px',
                  background: C.redFaint,
                  border: `1px solid ${C.red}40`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  color: C.red,
                  fontFamily: MONO,
                  fontSize: 8,
                  letterSpacing: '0.1em',
                }}
              >
                ÉCHEC
              </button>
            </div>
          )}

          <div style={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: MONO,
            fontSize: 9,
            color: C.textMuted,
            letterSpacing: '0.22em',
            pointerEvents: 'none',
          }}>
            ORBITE · ZOOM · CLIC
          </div>
        </main>

        {/* ═══ RIGHT PANEL ═══ */}
        <aside style={{
          width: 340,
          flexShrink: 0,
          background: C.bgPanel,
          borderLeft: `1px solid ${C.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            scrollbarWidth: 'none',
          }}>
            {/* Éditeur de position */}
            <AnimatePresence>
              {showEditor && (
                // layout : sans ce prop, Framer Motion ne mesure "height: auto"
                // qu'UNE fois, à l'ouverture — si le panneau d'écart de calibration
                // apparaît plus tard (une fois la calibration faite), sa hauteur
                // n'est jamais incluse et le conteneur reste figé à l'ancienne
                // taille, avec overflow:hidden : le panneau est là mais coupé,
                // invisible, et le scroll parent ne peut rien y faire. `layout`
                // fait recalculer la hauteur à chaque changement de contenu
                // (calibration terminée, curseurs masqués/affichés, etc.).
                <motion.div
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Crosshair size={9} color={C.violet} />
                      <span style={{ fontFamily: MONO, fontSize: 8, color: C.violet, letterSpacing: '0.12em' }}>
                        MA POSITION
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Masquer les curseurs sans fermer le panneau — utile
                          pour garder le panneau d'écart visible sans faire
                          défiler 6 curseurs au-dessus. */}
                      <button
                        onClick={() => setShowAxisSliders(v => !v)}
                        title={showAxisSliders ? 'Masquer les curseurs' : 'Afficher les curseurs'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          background: 'transparent',
                          border: `1px solid ${C.border}`,
                          borderRadius: 3,
                          color: C.textMuted,
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        {showAxisSliders ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                      </button>
                      <button onClick={() => setShowEditor(false)} style={{ color: C.textMuted, cursor: 'pointer' }}>
                        <X size={10} />
                      </button>
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {showAxisSliders && (
                      <motion.div
                        key="axis-sliders"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        style={{ overflow: 'hidden' }}
                      >
                        {config.axes?.map(axis => (
                          <AxisSlider
                            key={axis.id}
                            axis={axis}
                            value={selfPosition[axis.id]}
                            onChange={(v) => updateSelfPosition(axis.id, v)}
                          />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <CalibrationGapPanel
                    axes={config.axes || []}
                    selfPosition={selfPosition}
                    calibratedPosition={calibratedPosition}
                    onAdopt={handleAdoptCalibration}
                    onRecalibrate={handleResetCalibration}
                  />

                  <button
                    onClick={() => {
                      const initial = {};
                      config.axes.forEach(a => { initial[a.id] = 0.5; });
                      setActorPositions(prev => ({ ...prev, self: initial }));
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 0',
                      marginTop: 4,
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      borderRadius: 3,
                      color: C.textMuted,
                      fontFamily: MONO,
                      fontSize: 9,
                      letterSpacing: '0.1em',
                      cursor: 'pointer',
                    }}
                  >
                    RÉINITIALISER
                  </button>

                  <div style={{ height: 1, background: C.border, margin: '12px 0' }} />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Inspection */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Info size={9} color={C.textMuted} />
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.12em' }}>
                  INSPECTION
                </span>
              </div>

              {/* Trajectoire recommandée — déduite de la posture la plus
                  proche de votre position réelle, pas une liste statique à
                  parcourir : rend les trajectoires actionnables. */}
              {!selectedItem && recommendedTrajectory && mode === 'atelier' && (
                <button
                  onClick={() => setSelectedItem({ type: 'trajectory', data: recommendedTrajectory })}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    marginBottom: 10,
                    padding: '10px 12px',
                    background: recommendedTrajectory.color + '10',
                    border: `1px solid ${recommendedTrajectory.color}40`,
                    borderRadius: 5,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontFamily: MONO, fontSize: 7, color: recommendedTrajectory.color, letterSpacing: '0.1em', marginBottom: 4 }}>
                    TRAJECTOIRE RECOMMANDÉE · DEPUIS VOTRE POSTURE LA PLUS PROCHE
                  </div>
                  <div style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary }}>
                    {recommendedTrajectory.label}
                  </div>
                </button>
              )}

              <AnimatePresence mode="wait">
                {selectedItem?.type === 'archetype' && (
                  <ArchetypeCard
                    key={selectedItem.data.id}
                    archetype={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    config={config}
                    activeDomain={activeDomain}
                    onOpenLivret={openLivret} 
                  />
                )}
                {selectedItem?.type === 'trajectory' && (
                  <TrajectoryCard
                    key={selectedItem.data.id}
                    trajectory={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    config={config}
                    onOpenLivret={openLivret} 
                  />
                )}
                {selectedItem?.type === 'axis' && (
                  <AxisCard
                    key={selectedItem.data.id}
                    axis={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    onOpenLivret={openLivret} 
                  />
                )}
                {selectedItem?.type === 'tension' && (
                  <TensionCard
                    key={selectedItem.data.id}
                    tension={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    config={config}
                    onOpenLivret={openLivret} 
                  />
                )}
                {selectedItem?.type === 'simulation' && (
                  <SimulationCard
                    key={selectedItem.data.id}
                    simulation={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    onOpenLivret={openLivret} 
                  />
                )}
                {selectedItem?.type === 'scenario' && (
                  <ScenarioCard
                    key={selectedItem.data.id}
                    scenario={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    config={config}
                    activeDomain={activeDomain}
                    selfPosition={selfPosition}
                    onOpenLivret={openLivret} 
                  />
                )}
                {selectedItem?.type === 'bridge' && (
                  <BridgeCard
                    key={selectedItem.data.id}
                    bridge={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    config={config}
                    onOpenLivret={openLivret}
                  />
                )}
                {selectedItem?.type === 'trace' && (
                  <TraceCard
                    key={selectedItem.data.session_id}
                    trace={selectedItem.data}
                    onClose={() => setSelectedItem(null)}
                    onSave={handleSaveTrace}
                    onDownload={handleDownloadTrace}
                    onOpenLivret={openLivret} 
                  />
                )}
                {!selectedItem && (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                      padding: '20px 12px',
                      textAlign: 'center',
                      background: C.bgInset,
                      border: `1px solid ${C.border}`,
                      borderRadius: 4,
                    }}
                  >
                    <motion.div
                      animate={{ opacity: [0.15, 0.45, 0.15] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <Compass size={18} color={C.textMuted} style={{ margin: '0 auto 8px' }} />
                    </motion.div>
                    <p style={{ fontFamily: MONO, fontSize: 9, color: C.textMuted, lineHeight: 1.7 }}>
                      CLIQUEZ SUR UNE POSTURE,<br />UN AXE OU UNE TRAJECTOIRE
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Listes de référence */}
            {!selectedItem && mode === 'atelier' && (
              <>
                <div style={{ height: 1, background: C.border }} />

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Activity size={9} color={C.textMuted} />
                    <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.12em' }}>
                      POSTURES ({config.archetypes?.length || 0})
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {config.archetypes?.map(a => {
                      const rankIndex = rankedArchetypes.findIndex(r => r.archetype.id === a.id);
                      const rankData = rankIndex >= 0 ? rankedArchetypes[rankIndex] : null;
                      return (
                        <button
                          key={a.id}
                          onClick={() => setSelectedItem({ type: 'archetype', data: a })}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 10px',
                            background: hoveredTarget === a.id ? a.color + '15' : C.bgInset,
                            border: `1px solid ${hoveredTarget === a.id ? a.color + '40' : (rankData ? a.color + '40' : C.border)}`,
                            borderRadius: 3,
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.15s',
                          }}
                        >
                          <span style={{
                            fontFamily: MONO,
                            fontSize: 11,
                            color: a.color,
                            width: 14,
                            textAlign: 'center',
                          }}>
                            {a.icon}
                          </span>
                          <span style={{
                            fontFamily: MONO,
                            fontSize: 10,
                            color: C.textPrimary,
                            flex: 1,
                          }}>
                            {a.label}
                          </span>
                          {rankData && (
                            <span style={{
                              fontFamily: MONO,
                              fontSize: 7,
                              color: a.color,
                              letterSpacing: '0.08em',
                            }}>
                              #{rankIndex + 1} · {rankData.proximity}%
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {canSee('trajectories') && config.trajectories?.length > 0 && (
                  <>
                    <div style={{ height: 1, background: C.border }} />
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <GitBranch size={9} color={C.textMuted} />
                        <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.12em' }}>
                          TRAJECTOIRES
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        {config.trajectories.map(t => (
                          <button
                            key={t.id}
                            onClick={() => setSelectedItem({ type: 'trajectory', data: t })}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 10px',
                              background: C.bgInset,
                              border: `1px solid ${C.border}`,
                              borderRadius: 3,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <div style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: t.color,
                              flexShrink: 0,
                            }} />
                            <span style={{ fontFamily: MONO, fontSize: 10, color: C.textPrimary, flex: 1 }}>
                              {t.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {canSee('simulations') && config.simulations?.length > 0 && (
                  <>
                    <div style={{ height: 1, background: C.border }} />
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={9} color={C.textMuted} />
                        <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.12em' }}>
                          SIMULATIONS
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        {config.simulations.map(s => (
                          <button
                            key={s.id}
                            onClick={() => setSelectedItem({ type: 'simulation', data: s })}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 10px',
                              background: C.bgInset,
                              border: `1px solid ${C.border}`,
                              borderRadius: 3,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <Zap size={10} color={C.cyan} />
                            <span style={{ fontFamily: BODY, fontSize: 10, color: C.textPrimary, flex: 1, lineHeight: 1.4 }}>
                              {s.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>



          {/* Footer théorie */}
          {config.theoryRefs && (
            <div style={{
              padding: '10px 12px',
              borderTop: `1px solid ${C.border}`,
              flexShrink: 0,
              maxHeight: 110,
              overflowY: 'auto',
            }}>
              <div className="flex items-center gap-1.5 mb-2">
                <BookOpen size={8} color={C.textMuted} />
                <span style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em' }}>
                  CORPUS THÉORIQUE ({Object.keys(config.theoryRefs).length})
                </span>
                <HelpButton
                  onClick={() => openLivret('theories')}
                  title="Corpus théorique (Module 12)"
                />

              </div>
              <div style={{ fontFamily: BODY, fontSize: 10, color: C.textSecond, lineHeight: 1.5 }}>
                {Object.keys(config.theoryRefs).length} références mobilisées
              </div>
            </div>
          )}
        </aside>

      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════════════════════════════ */}
      <footer style={{
        height: 22,
        flexShrink: 0,
        background: C.bgPanel,
        borderTop: `1px solid ${C.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 14px',
      }}>
        <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em' }}>
          PESACORE · ATLAS DES POSTURES · {config.axes?.length || 0} AXES · {config.archetypes?.length || 0} POSTURES · {config.scenarios?.length || 0} SCÉNARIOS · {config.domains?.length || 0} DOMAINES
        </span>
        <div className="flex items-center gap-3">
          {currentTrace && (
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.cyan + '80' }}>
              SESSION ACTIVE · {currentTrace.positions_history?.length || 0} positions
            </span>
          )}
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted }}>
            {new Date().toISOString().split('T')[0]}
          </span>
        </div>
      </footer>

      <Livret
        open={livretOpen}
        onClose={() => setLivretOpen(false)}
        initialModuleId={livretModule}
        profile={activeProfile}
        />

      <AnimatePresence>
        {calibrationOpen && (
          <CalibrationModal
            axes={config.axes || []}
            answers={calibrationAnswers}
            onAnswer={handleCalibrationAnswer}
            onClose={() => setCalibrationOpen(false)}
          />
        )}
      </AnimatePresence>

    </div>
  );
};

export default VisualizationPostures;